import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Smartphone, Wifi, Battery, Cpu, CheckCircle, XCircle, ArrowUp, ArrowDown, X, Home, ArrowLeft, Grid, List, Instagram, Play, RotateCcw, WifiOff, Thermometer, Activity, HardDrive, Zap, Plus, Layers, Cast, Power, RefreshCw, Plane, Mail, Package, Square, CheckSquare, Trash2, Settings, Monitor, Server, Globe, PlusCircle, ChevronDown, Usb, ExternalLink } from 'lucide-react';
import useStickyPin from '../hooks/useStickyPin';
import axios from 'axios';
import { callApiForDevice } from '../utils/remoteApi';
import { useTheme } from '../contexts/ThemeContext';
import GmailModal from '../components/modals/GmailModal';
import InstagramModal from '../components/modals/InstagramModal';
import WorkflowModal from '../components/modals/WorkflowModal';
import SharedPageHeader from '../components/layout/PageHeader';
import { getProfilePicUrl, getLocalProfilePicUrl } from '../config/imageUrls';

const PageContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const WorkstationBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 16px; border-radius: 10px;
  background: var(--card-bg); border: 1px solid var(--card-border);
`;

const WsToggleGroup = styled.div`
  display: flex; border-radius: 8px; overflow: hidden;
  border: 1px solid var(--card-border); background: var(--surface);
`;

const WsToggleBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 6px 14px; border: none; cursor: pointer;
  font-size: 11px; font-weight: 700; transition: all .15s;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'var(--text-muted)'};
  &:hover { background: ${p => p.$active ? 'var(--primary)' : 'var(--surface-hover)'}; }
`;

const WsDot = styled.span`
  width: 7px; height: 7px; border-radius: 50%;
  background: ${p => p.$online ? 'var(--success)' : 'var(--error)'};
  flex-shrink: 0;
`;

const WsLabel = styled.span`
  font-size: 11px; font-weight: 600; color: var(--text-secondary);
`;

const WsAddBtn = styled.button`
  display: flex; align-items: center; gap: 4px; margin-left: auto;
  padding: 5px 12px; border-radius: 6px; border: 1px dashed var(--card-border);
  background: transparent; color: var(--text-muted); font-size: 10px; font-weight: 600;
  cursor: pointer; transition: all .15s;
  &:hover { border-color: var(--primary); color: var(--primary); }
`;

const WsAddModal = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0,0,0,.5); backdrop-filter: blur(4px);
`;

const WsAddCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border);
  border-radius: 14px; padding: 24px; width: 400px; max-width: 90vw;
  h3 { margin: 0 0 16px; color: var(--text-primary); font-size: 16px; }
  label { display: block; font-size: 11px; font-weight: 700; color: var(--text-muted);
    text-transform: uppercase; margin-bottom: 4px; margin-top: 12px; }
  input { width: 100%; padding: 8px 12px; border-radius: 8px; border: 1px solid var(--card-border);
    background: var(--surface); color: var(--text-primary); font-size: 13px; outline: none;
    &:focus { border-color: var(--primary); } }
`;

const WsAddActions = styled.div`
  display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px;
  button { padding: 8px 20px; border-radius: 8px; border: none; font-weight: 700;
    font-size: 12px; cursor: pointer; }
`;

const ViewToggle = styled.div`
  display: flex;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  overflow: hidden;
  margin-left: auto;
`;

const ViewToggleBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'var(--text-muted)'};

  &:hover {
    background: ${p => p.$active ? 'var(--primary)' : 'var(--surface-hover)'};
    color: ${p => p.$active ? '#fff' : 'var(--primary)'};
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const DCard = styled.div`
  background: var(--card-bg);
  border: 1px solid ${p => p.$connected ? 'var(--primary)' : 'var(--card-border)'};
  border-radius: 14px;
  padding: 20px;
  transition: all 0.25s ease;
  position: relative;
  overflow: hidden;

  ${p => p.$connected && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, var(--primary), var(--accent));
    }
  `}

  &:hover {
    border-color: var(--primary);
    box-shadow: var(--shadow-hover);
    transform: translateY(-2px);
  }
`;

const DCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const DCardName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DCardStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${p => p.$on ? 'rgba(34, 197, 94, 0.12)' : 'rgba(239, 68, 68, 0.12)'};
  color: ${p => p.$on ? 'var(--success)' : 'var(--error)'};
`;

const DCardDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${p => p.$on ? 'var(--success)' : 'var(--error)'};
`;

const DCardBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
`;

const DCardField = styled.div`
  font-size: 11px;
  color: var(--text-muted);
  
  span {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DCardActions = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
`;

const DCardActionBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: var(--primary);
    color: var(--primary);
    background: rgba(165, 145, 239, 0.08);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    &:hover {
      border-color: var(--border);
      color: var(--text-secondary);
      background: transparent;
    }
  }
`;

const DCardWorkflow = styled.span`
  font-size: 14px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 6px;
  color: var(--primary);
  background: rgba(165, 145, 239, 0.1);
  border: 1px solid rgba(165, 145, 239, 0.3);
`;

const BulkActionsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(165, 145, 239, 0.1);
  border: 2px solid var(--primary);
  border-radius: 12px;
  margin-bottom: 20px;
  animation: slideDown 0.3s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const BulkActionsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BulkActionsCount = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const BulkActionsButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const BulkActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--primary);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(165, 145, 239, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ClearSelectionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface-hover);
  }
`;

const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.checked ? 'var(--primary)' : 'var(--text-secondary)'};
  transition: all 0.2s ease;

  &:hover {
    color: var(--primary);
    transform: scale(1.1);
  }
`;

const DeleteButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 2px solid ${props => props.disabled ? 'rgba(239, 68, 68, 0.2)' : 'var(--error)'};
  background: ${props => props.disabled 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))'
  };
  color: ${props => props.disabled ? '#666' : 'var(--error)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(239, 68, 68, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.4s, height 0.4s;
  }

  &:hover::before {
    width: ${props => props.disabled ? '0' : '100px'};
    height: ${props => props.disabled ? '0' : '100px'};
  }

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 5px 15px rgba(239, 68, 68, 0.4)'};
    border-color: ${props => props.disabled ? 'rgba(239, 68, 68, 0.2)' : 'var(--error)'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(0)'};
  }
`;

const SettingsButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 2px solid ${props => props.disabled ? 'rgba(165, 145, 239, 0.2)' : 'var(--primary)'};
  background: ${props => props.disabled 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'linear-gradient(135deg, rgba(165, 145, 239, 0.2), rgba(168, 85, 247, 0.2))'
  };
  color: ${props => props.disabled ? '#666' : 'var(--primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(165, 145, 239, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.4s, height 0.4s;
  }

  &:hover::before {
    width: ${props => props.disabled ? '0' : '100px'};
    height: ${props => props.disabled ? '0' : '100px'};
  }

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px) rotate(90deg)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 5px 15px rgba(165, 145, 239, 0.4)'};
    border-color: ${props => props.disabled ? 'rgba(165, 145, 239, 0.2)' : 'var(--secondary)'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(0) rotate(90deg)'};
  }
`;

const DevicesTable = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  overflow: auto;
  flex: 1;
  min-height: 0;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 24px 32px 44px 32px minmax(80px,1fr) 32px 110px 95px minmax(90px,1.2fr) minmax(80px,1fr) 58px 95px 36px;
  gap: 4px;
  padding: 12px 12px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  font-weight: 700;
  color: var(--text-muted);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 1000px;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const TableHeaderCell = styled.div`
  cursor: pointer;
  user-select: none;
  display: flex;
  flex-direction: column;
  gap: 5px;
  transition: all 0.2s ease;
  padding: 5px;
  border-radius: 4px;

  &:hover {
    background: var(--surface-hover);
    color: var(--text-primary);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 4px 8px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 4px;
  color: var(--text-secondary);
  font-size: 11px;
  outline: none;
  margin-top: 4px;
  
  &:focus {
    border-color: var(--primary);
    background: var(--card-bg);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 24px 32px 44px 32px minmax(80px,1fr) 32px 110px 95px minmax(90px,1.2fr) minmax(80px,1fr) 58px 95px 36px;
  gap: 4px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  transition: background 0.1s;
  background: transparent;
  border-left: 3px solid ${props => props.connected ? 'var(--primary)' : 'transparent'};
  min-width: 1000px;
  align-items: center;

  &:hover {
    background: var(--surface-hover);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  color: var(--text-primary);
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.connected ? 'var(--success)' : 'var(--error)'};
  box-shadow: 0 0 6px ${props => props.connected ? 'rgba(34,197,94,.4)' : 'rgba(239,68,68,.4)'};
`;

const DeviceNameBadge = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 700;
  color: var(--primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CastButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 2px solid ${props => props.disabled ? 'rgba(165, 145, 239, 0.2)' : 'var(--primary)'};
  background: ${props => props.disabled 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'linear-gradient(135deg, rgba(165, 145, 239, 0.2), rgba(168, 85, 247, 0.2))'
  };
  color: ${props => props.disabled ? '#666' : 'var(--primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(165, 145, 239, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.4s, height 0.4s;
  }

  &:hover::before {
    width: ${props => props.disabled ? '0' : '100px'};
    height: ${props => props.disabled ? '0' : '100px'};
  }

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 5px 15px rgba(165, 145, 239, 0.4)'};
    border-color: ${props => props.disabled ? 'rgba(165, 145, 239, 0.2)' : 'var(--secondary)'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(0)'};
  }
`;

const RestartButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, var(--error) 0%, var(--error) 100%);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.4);
    background: linear-gradient(135deg, var(--error) 0%, #e63946 100%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PhoneScreenshot = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`;

const CastModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(165, 145, 239, 0.9);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
  overflow-y: auto;
  padding: 30px;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const CastWindow = styled.div`
  background: linear-gradient(135deg, #0f0f23 0%, var(--bg) 50%, var(--bg) 100%);
  border-radius: 24px;
  width: 95%;
  max-width: 1800px;
  height: auto;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 80px rgba(165, 145, 239, 0.7), 0 0 0 1px rgba(165, 145, 239, 0.2);
  border: 2px solid rgba(165, 145, 239, 0.4);
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--primary) 0%, var(--secondary) 50%, var(--accent) 100%);
  }

  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    to { 
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const CastHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 30px;
  border-bottom: 1px solid rgba(165, 145, 239, 0.15);
  background: linear-gradient(135deg, rgba(165, 145, 239, 0.08) 0%, rgba(168, 85, 247, 0.05) 100%);
  backdrop-filter: blur(10px);
`;

const CastTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const CastDeviceIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(165, 145, 239, 0.3);
`;

const CastDeviceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const CastDeviceName = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: var(--primary);
  margin: 0;
`;

const CastDeviceId = styled.div`
  font-size: 13px;
  color: #888;
  font-family: monospace;
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.3);
    transform: rotate(90deg);
  }
`;

const CastContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 20px;
  padding: 20px;
  background: rgba(165, 145, 239, 0.2);
`;

const CastLeftPanel = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px;
  background: linear-gradient(135deg, rgba(165, 145, 239, 0.3) 0%, rgba(165, 145, 239, 0.05) 100%);
  border-radius: 16px;
  border: 1px solid rgba(165, 145, 239, 0.2);
`;

const CastMiddlePanel = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 15px;
  justify-content: center;
`;

const MiddleControlButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 14px;
  border: 2px solid ${props => props.disabled ? 'rgba(165, 145, 239, 0.2)' : 'rgba(165, 145, 239, 0.4)'};
  background: ${props => props.disabled 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'linear-gradient(135deg, rgba(165, 145, 239, 0.2), rgba(168, 85, 247, 0.15))'
  };
  color: ${props => props.disabled ? '#666' : 'var(--accent)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(165, 145, 239, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.4s, height 0.4s;
  }

  &:hover::before {
    width: ${props => props.disabled ? '0' : '120px'};
    height: ${props => props.disabled ? '0' : '120px'};
  }

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'scale(1.1)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 8px 20px rgba(165, 145, 239, 0.4)'};
    border-color: ${props => props.disabled ? 'rgba(165, 145, 239, 0.2)' : 'var(--secondary)'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'scale(0.95)'};
  }

  svg {
    position: relative;
    z-index: 1;
  }
`;

const PhoneScreen = styled.div`
  position: relative;
  width: 320px;
  height: 600px;
  background: #000;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 15px 50px rgba(165, 145, 239, 0.6), 0 0 0 1px rgba(165, 145, 239, 0.3);
  border: 6px solid var(--bg);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const CoordinatesOverlay = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(165, 145, 239, 0.8);
  color: var(--success);
  padding: 8px 12px;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: bold;
  pointer-events: none;
  z-index: 1000;
  border: 1px solid rgba(0, 255, 0, 0.3);
  box-shadow: 0 2px 10px rgba(0, 255, 0, 0.2);
  display: ${props => props.visible ? 'block' : 'none'};
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 15px;
  width: 100%;
`;

const NavButton = styled.button`
  flex: 1;
  padding: 14px 20px;
  border: 2px solid rgba(165, 145, 239, 0.4);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(165, 145, 239, 0.2), rgba(168, 85, 247, 0.15));
  color: var(--accent);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: linear-gradient(135deg, rgba(165, 145, 239, 0.3), rgba(168, 85, 247, 0.2));
    border-color: var(--secondary);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(165, 145, 239, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CastRightPanel = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  background: rgba(165, 145, 239, 0.2);
  overflow-y: auto;
  padding: 10px;
`;

const ParamsSection = styled.div`
  padding: 15px 20px;
  border-bottom: 1px solid rgba(165, 145, 239, 0.1);
`;

const SectionTitle = styled.h3`
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  font-weight: 800;
  color: var(--border);
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  
  svg {
    color: var(--secondary);
    filter: drop-shadow(0 0 6px rgba(168, 85, 247, 0.4));
    width: 16px;
    height: 16px;
  }
`;

const ParamItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 6px;
  transition: all 0.3s ease;

  &:hover {
`;

const ParamLabel = styled.div`
display: flex;
align-items: center;
gap: 6px;
color: #aaa;
font-size: 12px;

svg {
width: 14px;
height: 14px;
}
`;

const ParamValue = styled.div`
color: #fff;
font-weight: 600;
font-size: 13px;
`;

const TempGauge = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  margin-top: 6px;
  position: relative;
`;

const TempFill = styled.div`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => {
    if (props.percentage > 80) return 'linear-gradient(90deg, var(--error), var(--error))';
    if (props.percentage > 60) return 'linear-gradient(90deg, var(--warning), var(--warning))';
    return 'linear-gradient(90deg, var(--success), var(--success))';
  }};
  transition: all 0.5s ease;
  box-shadow: 0 0 10px ${props => {
    if (props.percentage > 80) return 'rgba(255, 107, 107, 0.5)';
    if (props.percentage > 60) return 'rgba(255, 217, 61, 0.5)';
    return 'rgba(102, 226, 135, 0.5)';
  }};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 16px 20px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  justify-content: flex-start;
  box-shadow: 0 4px 12px rgba(165, 145, 239, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(165, 145, 239, 0.5);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    flex-shrink: 0;
  }

  > div {
    text-align: left;
    flex: 1;
  }
`;

const TerminalContainer = styled.div`
  background: var(--bg);
  border: 1px solid rgba(165, 145, 239, 0.3);
  border-radius: 8px;
  padding: 15px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: var(--success);
  overflow-y: auto;
  max-height: 400px;
  min-height: 200px;
  flex: 1;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(165, 145, 239, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(165, 145, 239, 0.5);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(165, 145, 239, 0.7);
  }
`;

const LogLine = styled.div`
  margin-bottom: 4px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  
  &.error {
    color: var(--error);
  }
  
  &.warning {
    color: var(--warning);
  }
  
  &.success {
    color: var(--success);
  }
  
  &.info {
    color: var(--info);
  }
`;

const ConnectionBtn = styled.button`
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${props => {
    if (props.variant === 'start') return 'linear-gradient(135deg, var(--success) 0%, var(--success) 100%)';
    if (props.variant === 'restart') return 'linear-gradient(135deg, var(--warning) 0%, var(--warning) 100%)';
    if (props.variant === 'plane-off') return 'linear-gradient(135deg, var(--success) 0%, var(--success) 100%)';
    if (props.variant === 'plane-on') return 'linear-gradient(135deg, var(--error) 0%, var(--error) 100%)';
    if (props.variant === 'close') return 'linear-gradient(135deg, var(--error) 0%, var(--error) 100%)';
    return 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)';
  }};
  box-shadow: 0 4px 12px ${props => {
    if (props.variant === 'start') return 'rgba(102, 226, 135, 0.3)';
    if (props.variant === 'restart') return 'rgba(255, 217, 61, 0.3)';
    if (props.variant === 'plane-off') return 'rgba(102, 226, 135, 0.3)';
    if (props.variant === 'plane-on') return 'rgba(255, 107, 107, 0.3)';
    if (props.variant === 'close') return 'rgba(255, 107, 107, 0.3)';
    return 'rgba(102, 126, 234, 0.3)';
  }};

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px ${props => {
      if (props.variant === 'start') return 'rgba(102, 226, 135, 0.5)';
      if (props.variant === 'restart') return 'rgba(255, 217, 61, 0.5)';
      if (props.variant === 'plane-off') return 'rgba(102, 226, 135, 0.5)';
      if (props.variant === 'plane-on') return 'rgba(255, 107, 107, 0.5)';
      if (props.variant === 'close') return 'rgba(255, 107, 107, 0.5)';
      return 'rgba(102, 126, 234, 0.5)';
    }};
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const Devices = ({ onSelectDevice, setMirrorOpen, activeWorkstation: activeWsProp, onWorkstationChange }) => {
  const { theme } = useTheme();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [devicePIDs, setDevicePIDs] = useState({});
  const [devicePowerStats, setDevicePowerStats] = useState({});
  const [sortBy, setSortBy] = useState('isConnected');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [castModalOpen, setCastModalOpen] = useState(false);
  const [deviceStats, setDeviceStats] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [runningScripts, setRunningScripts] = useState({}); // device_id -> boolean
  const [searchFilters, setSearchFilters] = useState({}); // Ajout de l'état de recherche
  const [gmailModalOpen, setGmailModalOpen] = useState(false);
  const [selectedGmailDevice, setSelectedGmailDevice] = useState(null);
  const [instagramModalOpen, setInstagramModalOpen] = useState(false);
  const [selectedInstagramDevice, setSelectedInstagramDevice] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]); // IDs des devices sélectionnés
  const [viewMode, setViewMode] = useState(() => {
    // Default to card view on mobile (<=768px) for better responsive UX
    if (typeof window !== 'undefined' && window.innerWidth <= 768) return 'card';
    return 'table';
  }); // 'table' or 'card'
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [apkModalOpen, setApkModalOpen] = useState(false);
  const [mouseCoords, setMouseCoords] = useState({ x: 0, y: 0, visible: false });
  const [availableApks, setAvailableApks] = useState([]);
  const [availableInstagramAccounts, setAvailableInstagramAccounts] = useState([]);
  const [selectedApk, setSelectedApk] = useState(null);
  const [deviceLogs] = useState([]);
  const [workstations, setWorkstations] = useState([]);
  // Use prop from App.js if available, fallback to local state
  const [localActiveWs, setLocalActiveWs] = useState('all');
  const activeWorkstation = activeWsProp || localActiveWs;
  const setActiveWorkstation = (v) => { setLocalActiveWs(v); onWorkstationChange?.(v); };
  const [wsAddModalOpen, setWsAddModalOpen] = useState(false);
  const [newWsName, setNewWsName] = useState('');
  const [newWsUrl, setNewWsUrl] = useState('');
  // Sticky pin sections
  const { sectionRef, getStickyStyle } = useStickyPin('devices', ['header', 'controls']);
  const [newDevice, setNewDevice] = useState({
    device_id: '',
    device_name: '',
    android_version: '',
    gmail_account: '',
    instagram_version: ''
  });

  // ADB Wireless state
  const [showWifiPanel, setShowWifiPanel] = useState(false);
  const [wifiPairIp, setWifiPairIp] = useState('');
  const [wifiPairPort, setWifiPairPort] = useState('');
  const [wifiPairCode, setWifiPairCode] = useState('');
  const [wifiConnectIp, setWifiConnectIp] = useState('');
  const [wifiConnectPort, setWifiConnectPort] = useState('5555');
  const [wifiStatus, setWifiStatus] = useState('');
  const [wifiPaired, setWifiPaired] = useState(false);

  // Sync pair IP → connect IP in real-time
  const handlePairIpChange = (val) => {
    setWifiPairIp(val);
    if (!wifiPaired) setWifiConnectIp(val);
  };

  // APK Installation state
  const [showApkPanel, setShowApkPanel] = useState(false);

  // Workstation system info
  const [, setWsInfo] = useState(null);
  const [, setWsInfoLoading] = useState(false);
  const [, setPcIp] = useState('');
  const [pcIps, setPcIps] = useState([]); // All PC interfaces {ip, netmask, name}

  // Returns true if deviceIp is on the same subnet as any known PC interface.
  // Falls back to true (no warning) when no interface info is available.
  const isOnSameSubnet = (deviceIp, interfaces) => {
    if (!interfaces.length || !/^\d+\.\d+\.\d+\.\d+$/.test(deviceIp)) return true;
    const toInt = s => s.split('.').reduce((a, o) => (a << 8) | parseInt(o, 10), 0) >>> 0;
    const devInt = toInt(deviceIp);
    return interfaces.some(({ ip, netmask }) => (toInt(ip) & toInt(netmask)) === (devInt & toInt(netmask)));
  };

  // Stats are computed after filteredDevices (defined below) — see statsForDisplay

  // Fetch local workstation system info
  const fetchWsInfo = useCallback(async () => {
    try {
      setWsInfoLoading(true);
      // For local or 'all', fetch directly; for remote, proxy via API
      if (!activeWorkstation || activeWorkstation === 'local' || activeWorkstation === 'all') {
        const r = await axios.get('/api/workstation-info');
        setWsInfo(r.data);
        // Pre-fill WiFi panel with PC's local IP
        if (r.data?.network?.ip) {
          const ip = r.data.network.ip;
          setPcIp(ip);
          setWifiPairIp(prev => (!prev || prev === '192.168.1.') ? ip : prev);
          setWifiConnectIp(prev => (!prev || prev === '192.168.1.') ? ip : prev);
        }
        if (r.data?.network?.ips?.length) {
          setPcIps(r.data.network.ips);
        }
      } else {
        const r = await axios.post(`/api/workstations/${activeWorkstation}/api-proxy`, {
          method: 'GET', path: '/api/workstation-info'
        });
        if (r.data.success && r.data.data) setWsInfo(r.data.data);
        else setWsInfo(null);
      }
    } catch { setWsInfo(null); }
    finally { setWsInfoLoading(false); }
  }, [activeWorkstation]);



  // Fetch workstations list
  const fetchWorkstations = useCallback(async () => {
    try {
      const r = await axios.get('/api/workstations');
      if (r.data.success) setWorkstations(r.data.workstations || []);
    } catch (e) { /* silent — table may not exist yet */ }
  }, []);

  const addWorkstation = async () => {
    if (!newWsName.trim() || !newWsUrl.trim()) return;
    try {
      await axios.post('/api/workstations', { name: newWsName.trim(), ws_url: newWsUrl.trim() });
      setWsAddModalOpen(false);
      setNewWsName('');
      setNewWsUrl('');
      fetchWorkstations();
    } catch (e) { alert('Erreur: ' + (e.response?.data?.error || e.message)); }
  };

  // Charger les devices depuis la base de données et vérifier les connexions
  useEffect(() => {
    fetchDevices();
    fetchInstagramAccounts(); // Charger les comptes Instagram disponibles
    fetchDevicePIDs();
    fetchWorkstations();
    fetchWsInfo();

    // Rafraîchir les devices toutes les 2 minutes (au lieu de 30 secondes)
    const interval = setInterval(() => {
      fetchDevices(true);
      fetchDevicePIDs();
    }, 120000);
    // Refresh ws info every 60s
    const wsInfoIv = setInterval(fetchWsInfo, 60000);

    return () => { clearInterval(interval); clearInterval(wsInfoIv); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch ws info when workstation changes
  useEffect(() => {
    fetchWsInfo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkstation]);

  // Vérifier si on doit ouvrir la modale de casting depuis l'URL
  useEffect(() => {
    if (devices.length === 0) return;

    const urlParams = new URLSearchParams(window.location.search);
    const castDeviceId = urlParams.get('cast');
    if (castDeviceId) {
      console.log(`🎬 Ouverture automatique du casting pour device: ${castDeviceId}`);
      const device = devices.find(d => d.device_id === castDeviceId);
      if (device) {
        openCastModal(device);
        // Nettoyer l'URL après avoir ouvert la modale
        window.history.replaceState({}, '', '/devices');
      } else {
        console.warn(`⚠️ Device ${castDeviceId} non trouvé pour le casting`);
      }
    }
  }, [devices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh power stats — only for devices on CURRENT workstation (avoids ADB errors for remote devices)
  useEffect(() => {
    if (devices.length === 0) return;
    
    const refreshPowerStats = () => {
      const localWsId = workstations.find(w => w.is_current)?.id;
      devices.forEach(async (device) => {
        if (!device.isConnected) return;
        // Skip devices on another workstation — they're not ADB-reachable locally
        if (localWsId && device.workstation_id && device.workstation_id !== localWsId) return;
        try {
          const powerResponse = await axios.get(`/api/devices/${device.device_id}/power`);
          setDevicePowerStats(prev => ({
            ...prev,
            [device.device_id]: {
              power: powerResponse.data.power || 0,
              temperature: powerResponse.data.temperature || 0
            }
          }));
        } catch (error) {
          // Silencieux
        }
      });
    };
    
    refreshPowerStats();
    const statsInterval = setInterval(refreshPowerStats, 30000);
    return () => clearInterval(statsInterval);
  }, [devices, workstations]);

  const fetchInstagramAccounts = async () => {
    try {
      const response = await axios.get('/api/instagram-accounts');
      if (response.data.success) {
        setAvailableInstagramAccounts(response.data.accounts);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des comptes Instagram:', error);
    }
  };

  const fetchDevicePIDs = async () => {
    try {
      const response = await axios.get('/api/pids');
      if (response.data.success) {
        const pidsMap = {};
        response.data.pids.forEach(pidInfo => {
          pidsMap[pidInfo.deviceId] = pidInfo;
        });
        setDevicePIDs(pidsMap);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des PIDs:', error);
    }
  };

  const fetchDevices = async (isRefresh = false) => {
    try {
      // Only show loading spinner on initial load, not background refreshes
      if (!isRefresh && devices.length === 0) {
        setLoading(true);
      }
      const response = await axios.get('/api/devices');
      
      if (response.data && Array.isArray(response.data.devices)) {
        console.log(`✅ ${response.data.devices.length} devices récupérés`);
        setDevices(response.data.devices);
        return response.data.devices;
      } else {
        console.error('❌ Format de réponse incorrect:', response.data);
        throw new Error('Format de réponse incorrect');
      }
    } catch (error) {
      console.error('❌ Erreur chargement devices:', error.message);
      // Only set fallback data if we have nothing yet
      if (devices.length === 0) {
        setDevices([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchScriptStatuses = async () => {
    if (devices.length === 0) return;
    
    try {
      const statusPromises = devices.map(device => 
        axios.get(`/api/script-status/${device.device_id}`)
          .then(res => ({ device_id: device.device_id, running: res.data.running }))
          .catch(() => ({ device_id: device.device_id, running: false }))
      );
      
      const statuses = await Promise.all(statusPromises);
      const statusMap = {};
      statuses.forEach(({ device_id, running }) => {
        statusMap[device_id] = running;
      });
      
      setRunningScripts(statusMap);
    } catch (error) {
      console.error('❌ Erreur lors de la vérification des statuts:', error);
    }
  };

  const checkConnectionStatus = async () => {
    try {
      console.log('🔍 Vérification des connexions ADB en cours...');
      await axios.post('/api/devices/check-connections');
      
      // Re-fetch full device list from DB (picks up new devices + updated workstation_id + isConnected)
      await fetchDevices();
      console.log('✅ Vérification terminée — liste des devices rafraîchie');
    } catch (error) {
      console.error('❌ Erreur lors de la vérification des connexions:', error);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Fonction pour lancer le script Gmail avec ou sans restarteur
  const handleGmailLaunch = async (device_id, withRestarter, closeAppsAfter) => {
    try {
      const device = devices.find(d => d.device_id === device_id);
      const responseData = await callApiForDevice(device, '/api/devices/create-gmail', 'POST', {
        device_id: device_id,
        with_restarter: withRestarter,
        close_apps_after: closeAppsAfter
      });
      const response = { data: responseData };
      
      if (response.data.success) {
        console.log('✅ Création Gmail lancée:', response.data);
        alert(`✅ Terminal ouvert pour la création Gmail!\n\nDevice: ${response.data.deviceId || device_id}\n${withRestarter ? '⚡ Restarteur lancé en premier\n' : ''}${closeAppsAfter ? '🗑️ Applications fermées après\n' : ''}\nSuivez la progression dans le terminal qui vient de s'ouvrir.`);
        setGmailModalOpen(false);
        setSelectedGmailDevice(null);
      }
    } catch (error) {
      console.error('❌ Erreur création Gmail:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`❌ Erreur lors du lancement de la création Gmail:\n\n${errorMsg}`);
    }
  };

  // Fonction pour lancer le script de déconnexion Gmail
  const handleGmailDisconnect = async (device_id, device_name) => {
    try {
      const device = devices.find(d => d.device_id === device_id);
      const responseData = await callApiForDevice(device, '/api/devices/disconnect-gmail', 'POST', {
        device_id: device_id,
        device_name: device_name
      });
      const response = { data: responseData };
      
      if (response.data.success) {
        console.log('✅ Déconnexion Gmail lancée:', response.data);
        alert(`✅ Terminal ouvert pour la déconnexion Gmail!\n\nDevice: ${device_name || device_id}\n\nLe script va déconnecter tous les comptes Gmail sauf le compte principal.\nSuivez la progression dans le terminal qui vient de s'ouvrir.`);
        setGmailModalOpen(false);
        setSelectedGmailDevice(null);
      }
    } catch (error) {
      console.error('❌ Erreur déconnexion Gmail:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`❌ Erreur lors du lancement de la déconnexion Gmail:\n\n${errorMsg}`);
    }
  };

  // Fonction pour détecter et mettre à jour le compte Gmail principal
  const handleUpdateMainGmailAccount = async (device_id, device_name) => {
    try {
      const device = devices.find(d => d.device_id === device_id);
      const responseData = await callApiForDevice(device, '/api/devices/detect-main-gmail', 'POST', {
        device_id: device_id
      });
      const response = { data: responseData };
      
      if (response.data.success) {
        console.log('✅ Détection du compte Gmail principal lancée:', response.data);
        alert(`✅ Terminal ouvert pour la détection du compte Gmail principal!\n\nDevice: ${device_name || device_id}\n\nLe script va:\n1. Ouvrir Gmail\n2. Cliquer sur l'icône de profil\n3. Scanner tous les comptes présents\n4. Détecter le compte principal (en haut de la liste)\n5. Mettre à jour la table devices\n\nSuivez la progression dans le terminal qui vient de s'ouvrir.`);
        setGmailModalOpen(false);
        setSelectedGmailDevice(null);
        
        // Rafraîchir les devices après 10 secondes pour voir la mise à jour
        setTimeout(() => {
          fetchDevices();
        }, 10000);
      }
    } catch (error) {
      console.error('❌ Erreur détection compte Gmail principal:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`❌ Erreur lors du lancement de la détection:\n\n${errorMsg}`);
    }
  };

  // Fonction pour lancer le script Instagram avec ou sans restarteur
  const handleInstagramLaunch = async (device_id, account_id, withRestarter, closeAppsAfter, debugRecording, bigScrollCount) => {
    try {
      const device = devices.find(d => d.device_id === device_id);
      const responseData = await callApiForDevice(device, '/api/devices/start-interact', 'POST', {
        device_id: device_id,
        account_id: account_id,
        with_restarter: withRestarter,
        close_apps_after: closeAppsAfter,
        debug_recording: debugRecording,
        big_scroll_count: bigScrollCount || 3
      });
      const response = { data: responseData };
      
      if (response.data.success) {
        console.log('✅ Script Instagram lancé:', response.data);
        alert(`✅ Terminal ouvert pour le script Instagram!\n\nDevice: ${response.data.device_name || device_id}\nCompte: ${response.data.username || account_id}\n${withRestarter ? '⚡ Restarteur lancé en premier\n' : ''}${closeAppsAfter ? '🗑️ Applications fermées après\n' : ''}${debugRecording ? '🎬 Mode Debug Recording activé\n' : ''}\nSuivez la progression dans le terminal qui vient de s'ouvrir.`);
        setInstagramModalOpen(false);
        setSelectedInstagramDevice(null);
      }
    } catch (error) {
      console.error('❌ Erreur script Instagram:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`❌ Erreur lors du lancement du script Instagram:\n\n${errorMsg}`);
    }
  };

  // Fonctions de gestion de sélection multiple
  const toggleDeviceSelection = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const selectAllDevices = () => {
    setSelectedDevices(filteredDevices.map(d => d.device_id));
  };

  const clearSelection = () => {
    setSelectedDevices([]);
  };

  const handleWorkflowLaunch = async (deviceIds, scriptIds) => {
    try {
      console.log('🚀 Lancement du workflow:', { deviceIds, scriptIds });
      
      // TODO: Implémenter l'appel API pour lancer le workflow
      alert(`Workflow lancé!\n\n${deviceIds.length} device(s)\n${scriptIds.length} script(s)\n\nFonctionnalité en cours d'implémentation...`);
      
      setWorkflowModalOpen(false);
      clearSelection();
    } catch (error) {
      console.error('❌ Erreur workflow:', error);
      alert(`❌ Erreur lors du lancement du workflow:\n\n${error.message}`);
    }
  };

  // Charger la liste des APK disponibles
  const fetchAvailableApks = async () => {
    try {
      const response = await axios.get('/api/apks/list');
      if (response.data.success) {
        setAvailableApks(response.data.apks);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des APK:', error);
      setAvailableApks([]);
    }
  };

  // Charger les APK quand le modal s'ouvre
  useEffect(() => {
    if (apkModalOpen) {
      fetchAvailableApks();
    }
  }, [apkModalOpen]);

  // Ouvrir le ScreenMirrorPanel au lieu du cast modal
  const openCastModal = async (device) => {
    if (!device.isConnected) {
      alert('Ce device n\'est pas connecté. Veuillez le connecter d\'abord.');
      return;
    }
    
    try {
      // Récupérer les informations complètes du device (avec IP et compte Instagram)
      console.log(`🔍 Récupération des infos complètes pour ${device.device_id}...`);
      const response = await axios.get(`/api/devices/${device.device_id}`);
      const deviceWithFullInfo = {
        ...device,
        ...response.data
      };
      
      console.log(`✅ Infos récupérées:`, deviceWithFullInfo);
      // Select device in sidebar (this will update the bubble selection)
      if (onSelectDevice) {
        onSelectDevice(deviceWithFullInfo);
      }
      // Open the ScreenMirrorPanel
      if (setMirrorOpen) {
        setMirrorOpen(true);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des infos du device:', error);
      // En cas d'erreur, utiliser les données de base
      if (onSelectDevice) {
        onSelectDevice(device);
      }
      if (setMirrorOpen) {
        setMirrorOpen(true);
      }
    }
  };

  // Fermer la fenêtre de casting
  const closeCastModal = () => {
    setCastModalOpen(false);
    setSelectedDevice(null);
    setDeviceStats(null);
    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  };

  // Charger la résolution du device
  const [deviceResolutions] = useState({});
  
  // Gérer les actions des boutons de contrôle du device
  const handleDeviceAction = async (deviceId, action) => {
    try {
      console.log(`🎮 Action ${action} sur device ${deviceId}`);
      
      const actionMap = {
        'start_connection': 'start-connection',
        'restart': 'restart',
        'airplane_on': 'airplane-on',
        'airplane_off': 'airplane-off',
        'close_apps': 'close-apps'
      };

      // Find the device object for remote routing
      const device = devices.find(d => d.device_id === deviceId);
      const result = await callApiForDevice(
        device,
        '/api/device-action',
        'POST',
        { device_id: deviceId, action: actionMap[action] || action }
      );

      if (result?.success) {
        console.log(`✅ Action ${action} exécutée avec succès`);
      }
    } catch (error) {
      console.error(`❌ Erreur lors de l'action ${action}:`, error);
    }
  };

  // Gérer le tap sur l'écran avec résolution précise
  const handleScreenTap = async (event, deviceId) => {
    const img = event.target;
    const rect = img.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    
    // Utiliser la résolution réelle du device
    const resolution = deviceResolutions[deviceId] || { width: 1080, height: 2400 };
    
    const finalX = Math.round((clickX / rect.width) * resolution.width);
    const finalY = Math.round((clickY / rect.height) * resolution.height);
    
    console.log(`🎯 Tap sur ${deviceId}: clic(${Math.round(clickX)},${Math.round(clickY)}) -> device(${finalX},${finalY}) [résolution: ${resolution.width}x${resolution.height}]`);
    
    try {
      const device = devices.find(d => d.device_id === deviceId);
      await callApiForDevice(device, '/api/phone-cast/command', 'POST', {
        device_id: deviceId,
        command: 'tap',
        params: `${finalX} ${finalY}`
      });
    } catch (error) {
      console.error('Erreur lors du tap:', error);
    }
  };

  // Get current workstation ID for 'local' filtering
  const currentWsId = workstations.find(w => w.is_current)?.id;

  // Filtrer les devices selon les recherches + workstation active
  const filteredDevices = devices.filter(device => {
    // Workstation filter
    if (activeWorkstation === 'local') {
      // If no workstations loaded or SQLite/standalone mode (id='local'), show all devices
      if (!currentWsId || currentWsId === 'local') return true;
      if (device.workstation_id !== currentWsId) {
        return false;
      }
    } else if (activeWorkstation !== 'all') {
      // Specific remote workstation selected
      const selectedWsId = parseInt(activeWorkstation, 10);
      const selectedWs = workstations.find(w => w.id === selectedWsId);
      const remoteRealId = selectedWs?.remote_ws_id;
      // Match devices tagged with EITHER the local entry ID or the remote's real ID
      // Guard against null remoteRealId to prevent null === null leakage
      const matchesLocal = device.workstation_id === selectedWsId;
      const matchesRemote = remoteRealId != null && device.workstation_id === remoteRealId;
      if (!matchesLocal && !matchesRemote) {
        return false;
      }
    }
    // All other filters (search)
    return Object.keys(searchFilters).every(key => {
      const filterValue = searchFilters[key]?.toLowerCase() || '';
      if (!filterValue) return true;
      
      const deviceValue = String(device[key] || '').toLowerCase();
      return deviceValue.includes(filterValue);
    });
  });

  // Calculer les statistiques basées sur le filtre workstation actif
  const stats = {
    total: filteredDevices.length,
    connected: filteredDevices.filter(d => d.isConnected).length,
    disconnected: filteredDevices.filter(d => !d.isConnected).length
  };

  const sortedDevices = [...filteredDevices].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    // Gérer les valeurs null/undefined
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    if (sortBy === 'isConnected') {
      aValue = a.isConnected ? 1 : 0;
      bValue = b.isConnected ? 1 : 0;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  if (loading) {
    return (
      <PageContainer>
        <SharedPageHeader icon={Smartphone} title="Devices" subtitle="Appareils connectés" />
        <div style={{ textAlign: 'center', padding: '50px', color: 'var(--text-muted)' }}>
          Chargement des devices...
        </div>
      </PageContainer>
    );
  }

  // ── Onboarding screen when no device is known ──────────────────────────────
  if (devices.length === 0) {
    return (
      <PageContainer style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '82vh', gap: 36 }}>

        {/* Brand */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 52, fontWeight: 900, letterSpacing: -2,
            background: 'linear-gradient(135deg, var(--primary), var(--secondary), #48dbfb)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            marginBottom: 8, lineHeight: 1
          }}>
            QLICKR
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 500 }}>
            Automatisez vos réseaux sociaux intelligemment
          </div>
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 760 }}>
          {[
            {
              num: 1,
              icon: <Settings size={18} color="var(--primary)" />,
              title: 'Activer le mode développeur',
              desc: 'Paramètres → À propos du téléphone → Tapez 7 fois sur "Numéro de build"'
            },
            {
              num: 2,
              icon: <Cpu size={18} color="#f59e0b" />,
              title: 'Activer le débogage USB',
              desc: 'Paramètres → Options pour les développeurs → Débogage USB → Activer'
            },
            {
              num: 3,
              icon: <Smartphone size={18} color="var(--success)" />,
              title: 'Connecter votre appareil',
              desc: 'Branchez votre téléphone via USB et autorisez le débogage sur l\'écran du téléphone'
            }
          ].map(step => (
            <div key={step.num} style={{
              flex: '1 1 200px', maxWidth: 230,
              background: 'var(--card-bg)', border: '1px solid var(--card-border)',
              borderRadius: 14, padding: '20px 18px', textAlign: 'center'
            }}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                color: '#fff', fontWeight: 800, fontSize: 15,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 12
              }}>{step.num}</div>
              <div style={{ marginBottom: 8 }}>{step.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', marginBottom: 6 }}>
                {step.title}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                {step.desc}
              </div>
            </div>
          ))}
        </div>

        {/* Docs link */}
        <a
          href="https://docs.qlickr.io"
          target="_blank" rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, color: 'var(--primary)', fontWeight: 600,
            textDecoration: 'none', marginTop: -12
          }}
        >
          <ExternalLink size={14} />
          Voir la documentation complète
        </a>

        {/* Tips */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            <span style={{ color: 'var(--warning, #f59e0b)', fontWeight: 700 }}>💡 Tip :</span>{' '}
            Vous pouvez aussi utiliser un émulateur Android comme LDPlayer ou BlueStacks
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            <span style={{ color: 'var(--warning, #f59e0b)', fontWeight: 700 }}>💡</span>{' '}
            Assurez-vous d\'avoir les drivers ADB installés sur votre ordinateur
          </div>
        </div>

        {/* Waiting indicator */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <button
            onClick={checkConnectionStatus}
            style={{
              position: 'relative', background: 'none', border: 'none',
              cursor: 'pointer', padding: 0
            }}
            title="Vérifier la connexion"
          >
            <Usb size={44} color="var(--text-muted)" strokeWidth={1.5} />
            <span style={{
              position: 'absolute', top: -4, right: -8,
              background: 'var(--primary)', color: '#fff',
              fontSize: 10, fontWeight: 800,
              width: 18, height: 18, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>0</span>
          </button>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>
            En attente d\'un appareil...
          </div>
          <button
            onClick={checkConnectionStatus}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 20px', borderRadius: 8,
              border: '1px solid var(--primary)', background: 'rgba(165,145,239,.08)',
              color: 'var(--primary)', fontWeight: 700, fontSize: 12, cursor: 'pointer'
            }}
          >
            <RefreshCw size={13} /> Vérifier la connexion
          </button>
        </div>

      </PageContainer>
    );
  }

  // Ajouter un nouveau device
  const handleAddDevice = async () => {
    try {
      const response = await axios.post('/api/devices', newDevice);
      if (response.data.success) {
        alert('Device ajouté avec succès !');
        setAddModalOpen(false);
        setNewDevice({
          device_id: '',
          device_name: '',
          android_version: '',
          gmail_account: '',
          instagram_version: ''
        });
        fetchDevices();
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du device:', error);
      alert('Erreur lors de l\'ajout du device');
    }
  };

  return (
    <PageContainer>
      <SharedPageHeader icon={Smartphone} title="Devices" subtitle="Appareils connectés" />

      {/* ── Workstation Toggle (moved above stats for better flow) ── */}
      <WorkstationBar ref={sectionRef('header')} style={{ marginBottom: 16, ...getStickyStyle('header'), background: 'var(--background)', boxShadow: '0 16px 0 var(--background)' }}>
        <Server size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
        <WsLabel>Workstation :</WsLabel>
        <WsToggleGroup>
          <WsToggleBtn $active={activeWorkstation === 'all'} onClick={() => setActiveWorkstation('all')}>
            <Layers size={12} /> Tous
          </WsToggleBtn>
          <WsToggleBtn $active={activeWorkstation === 'local'} onClick={() => setActiveWorkstation('local')}>
            <Monitor size={12} /> Ce PC
          </WsToggleBtn>
          {workstations.filter(w => !w.is_current).map(w => (
            <WsToggleBtn key={w.id} $active={activeWorkstation === w.id.toString()} onClick={() => {
              setActiveWorkstation(w.id.toString());
              // Trigger remote device check when selecting a remote workstation
              axios.post(`/api/workstations/${w.id}/check-devices`).catch(() => {});
            }}>
              <WsDot $online={w.live_connected} />
              {w.name}
              {w.device_count > 0 && <span style={{ opacity: .5, fontSize: 9 }}>({w.device_count})</span>}
            </WsToggleBtn>
          ))}
        </WsToggleGroup>
        {activeWorkstation !== 'all' && activeWorkstation !== 'local' && (
          <WsLabel style={{ color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Globe size={10} /> Contrôle distant via WebSocket
          </WsLabel>
        )}
        <WsAddBtn onClick={() => setWsAddModalOpen(true)}>
          <PlusCircle size={10} /> Ajouter Workstation
        </WsAddBtn>
      </WorkstationBar>

      {/* ── Stats (scrolls normally) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, paddingBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Inline stats bar */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
            <div style={{ flex: 1, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg, var(--primary), var(--secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Layers size={18} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{stats.total}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>TOTAL</div>
              </div>
            </div>
            <div style={{ flex: 1, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg, var(--success), var(--success))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle size={18} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{stats.connected}</div>
                <div style={{ fontSize: 10, color: 'var(--success)', fontWeight: 600 }}>{((stats.connected / stats.total) * 100 || 0).toFixed(0)}% EN LIGNE</div>
              </div>
            </div>
            <div style={{ flex: 1, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg, var(--error), var(--error))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <XCircle size={18} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{stats.disconnected}</div>
                <div style={{ fontSize: 10, color: 'var(--error)', fontWeight: 600 }}>{((stats.disconnected / stats.total) * 100 || 0).toFixed(0)}% HORS LIGNE</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compact action bar — sticky */}
      <div ref={sectionRef('controls')} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: 'var(--background)', paddingBottom: 8, paddingTop: 4, ...getStickyStyle('controls') }}>
            <button onClick={checkConnectionStatus} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--primary)', background: 'rgba(165,145,239,.08)', color: 'var(--primary)', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
              <RefreshCw size={13} /> Vérifier
            </button>
            <button onClick={() => setAddModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--gradient)', color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
              <Plus size={13} /> Ajouter
            </button>
            <button onClick={() => setShowWifiPanel(!showWifiPanel)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid ${showWifiPanel ? 'var(--primary)' : 'var(--card-border)'}`, background: showWifiPanel ? 'rgba(165,145,239,.12)' : 'var(--card-bg)', color: showWifiPanel ? 'var(--primary)' : 'var(--text-secondary)', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>
              <Wifi size={13} /> Wi-Fi ADB
            </button>
            <button onClick={() => setShowApkPanel(!showApkPanel)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: `1px solid ${showApkPanel ? 'var(--primary)' : 'var(--card-border)'}`, background: showApkPanel ? 'rgba(165,145,239,.12)' : 'var(--card-bg)', color: showApkPanel ? 'var(--primary)' : 'var(--text-secondary)', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>
              <Package size={13} /> Apps
            </button>
            <div style={{ marginLeft: 'auto' }}>
              <ViewToggle>
                <ViewToggleBtn $active={viewMode === 'table'} onClick={() => setViewMode('table')} title="Vue liste">
                  <List size={16} />
                </ViewToggleBtn>
                <ViewToggleBtn $active={viewMode === 'card'} onClick={() => setViewMode('card')} title="Vue cartes">
                  <Grid size={16} />
                </ViewToggleBtn>
              </ViewToggle>
            </div>
          </div>

      {/* ── ADB Wi-Fi Connection Panel ── */}
      {showWifiPanel && (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Wifi size={16} style={{ color: 'var(--primary)' }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>Wi-Fi Connection (ADB)</span>
          </div>

          {/* Step 1: Pair (Android 11+) */}
          <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 14, marginBottom: 12, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Step 1: Pair device (Android 11+ only)
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
              Paramètres → Options développeur → Débogage sans fil → Associer l'appareil avec un code
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={wifiPairIp} onChange={e => handlePairIpChange(e.target.value)} placeholder="192.168.1.xxx" style={{ flex: 2, minWidth: 130, padding: '8px 12px', borderRadius: 8, border: `1px solid ${wifiPairIp && !isOnSameSubnet(wifiPairIp, pcIps) ? 'var(--error)' : 'var(--card-border)'}`, background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13 }} />
              <input value={wifiPairPort} onChange={e => setWifiPairPort(e.target.value)} placeholder="Port pairing" style={{ width: 90, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13 }} />
              <input value={wifiPairCode} onChange={e => setWifiPairCode(e.target.value)} placeholder="Code" style={{ width: 80, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13 }} />
              <button onClick={async () => {
                setWifiStatus('⏳ Pairing en cours (kill-server + pair)...');
                try {
                  const r = await axios.post('/api/devices/adb-wireless/pair', { 
                    ip: wifiPairIp, port: wifiPairPort, code: wifiPairCode,
                    connectPort: wifiConnectPort || undefined
                  });
                  if (r.data.success) {
                    setWifiConnectIp(wifiPairIp);
                    setWifiPaired(true);
                    if (r.data.connectOutput && r.data.connectOutput.toLowerCase().includes('connected')) {
                      setWifiStatus('✅ Paired + Connected automatiquement !');
                      fetchDevices();
                    } else {
                      setWifiStatus(r.data.hint || '✅ Paired ! Maintenant faites Connect ci-dessous.');
                    }
                  } else {
                    setWifiStatus(`❌ ${r.data.output}`);
                  }
                } catch (e) { setWifiStatus(`❌ ${e.message}`); }
              }} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--gradient)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Pair
              </button>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.4 }}>
              💡 Le port et le code changent à chaque ouverture de la fenêtre de pairing. Soyez rapide !
            </div>
            {wifiPairIp && !isOnSameSubnet(wifiPairIp, pcIps) && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.3)', fontSize: 11, color: 'var(--error)', lineHeight: 1.5 }}>
                ⚠️ <strong>{wifiPairIp}</strong> n'est PAS sur le même réseau que ce PC ({pcIps.map(i => i.ip).join(', ')}).<br/>
                → <strong>Désactive les données mobiles</strong> sur le téléphone, puis ré-ouvre "Débogage sans fil".
              </div>
            )}
          </div>

          {/* Step 2: Connect */}
          <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 14, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Step 2: Connect to device
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
              Port de connexion Wi-Fi = celui affiché sur la page "Débogage sans fil" (PAS le port de pairing). Pour les anciens appareils : port 5555.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={wifiConnectIp} onChange={e => { if (!wifiPaired) setWifiConnectIp(e.target.value); }} placeholder="192.168.1.xxx" readOnly={wifiPaired} style={{ flex: 2, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: wifiPaired ? 'var(--surface)' : 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, opacity: wifiPaired ? 0.7 : 1 }} />
              <input value={wifiConnectPort} onChange={e => setWifiConnectPort(e.target.value)} placeholder="5555" style={{ width: 90, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13 }} />
              <button onClick={async () => {
                setWifiStatus('⏳ Connexion...');
                try {
                  const r = await axios.post('/api/devices/adb-wireless/connect', { ip: wifiConnectIp, port: wifiConnectPort });
                  if (r.data.success) {
                    const mergeMsg = r.data.mergedFrom ? ` (fusionné avec ${r.data.mergedFrom})` : '';
                    setWifiStatus(`✅ Connecté à ${r.data.target}${mergeMsg}`);
                    fetchDevices();
                  } else {
                    setWifiStatus(`❌ ${r.data.output}`);
                  }
                } catch (e) { setWifiStatus(`❌ ${e.message}`); }
              }} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, var(--success), var(--success))', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                + Connect
              </button>
            </div>
          </div>

          {wifiStatus && (
            <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, background: wifiStatus.startsWith('✅') ? 'rgba(34,197,94,.1)' : wifiStatus.startsWith('❌') ? 'rgba(239,68,68,.1)' : 'rgba(165,145,239,.1)', color: wifiStatus.startsWith('✅') ? 'var(--success)' : wifiStatus.startsWith('❌') ? 'var(--error)' : 'var(--primary)', fontSize: 12, fontWeight: 600 }}>
              {wifiStatus}
            </div>
          )}
        </div>
      )}

      {/* ── APK Installation & Play Store Panel ── */}
      {showApkPanel && (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Package size={16} style={{ color: 'var(--primary)' }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>Install Applications</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
              {stats.connected} device{stats.connected > 1 ? 's' : ''} connecté{stats.connected > 1 ? 's' : ''}
            </span>
          </div>

          {/* Quick actions row */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {/* Freeze Play Store button */}
            <button onClick={async () => {
              const connected = devices.filter(d => d.isConnected);
              if (connected.length === 0) { alert('Aucun device connecté'); return; }
              if (!window.confirm(`Geler le Play Store sur ${connected.length} device(s) connecté(s) ?`)) return;
              let ok = 0;
              for (const d of connected) {
                try {
                  await axios.post('/api/devices/freeze-playstore', { device_id: d.device_id });
                  ok++;
                } catch (e) { /* silent */ }
              }
              alert(`✅ Play Store gelé sur ${ok}/${connected.length} devices`);
            }} style={{ padding: '10px 18px', borderRadius: 8, border: '2px solid var(--error)', background: 'rgba(239,68,68,.08)', color: 'var(--error)', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <XCircle size={14} /> Geler Play Store + MAJ auto
            </button>
            <button onClick={async () => {
              const connected = devices.filter(d => d.isConnected);
              if (connected.length === 0) { alert('Aucun device connecté'); return; }
              if (!window.confirm(`Dégeler le Play Store sur ${connected.length} device(s) ?`)) return;
              let ok = 0;
              for (const d of connected) {
                try {
                  await axios.post('/api/devices/unfreeze-playstore', { device_id: d.device_id });
                  ok++;
                } catch (e) { /* silent */ }
              }
              alert(`✅ Play Store dégelé sur ${ok}/${connected.length} devices`);
            }} style={{ padding: '10px 18px', borderRadius: 8, border: '2px solid var(--success)', background: 'rgba(34,197,94,.08)', color: 'var(--success)', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle size={14} /> Dégeler Play Store
            </button>
          </div>

          {/* Known apps to install */}
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            Applications à installer
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {[
              { name: 'Instagram', pkg: 'com.instagram.android', icon: '📸', color: '#E1306C' },
              { name: 'ADB Keyboard', pkg: 'com.android.adbkeyboard', icon: '⌨️', color: 'var(--primary)' },
              { name: 'TikTok', pkg: 'com.zhiliaoapp.musically', icon: '🎵', color: '#000' },
            ].map(app => (
              <div key={app.pkg} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 24 }}>{app.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{app.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{app.pkg}</div>
                </div>
                <button onClick={async () => {
                  const connected = selectedDevices.length > 0
                    ? devices.filter(d => selectedDevices.includes(d.device_id) && d.isConnected)
                    : devices.filter(d => d.isConnected);
                  if (connected.length === 0) { alert('Aucun device connecté sélectionné'); return; }
                  // Check available APKs matching this app
                  try {
                    const r = await axios.get('/api/devices/apk/available');
                    const matching = r.data.apks?.filter(a => a.name.toLowerCase().includes(app.name.toLowerCase())) || [];
                    if (matching.length === 0) {
                      alert(`Aucun APK trouvé pour ${app.name}.\nPlacez le fichier .apk dans le dossier 'apks/' du projet.`);
                      return;
                    }
                    if (!window.confirm(`Installer ${matching[0].filename} (${matching[0].sizeMB} MB) sur ${connected.length} device(s) ?`)) return;
                    let ok = 0;
                    for (const d of connected) {
                      try {
                        await axios.post('/api/devices/apk/install', { device_id: d.device_id, apk_path: matching[0].path });
                        ok++;
                      } catch (e) { /* silent */ }
                    }
                    alert(`✅ ${app.name} installé sur ${ok}/${connected.length} devices`);
                  } catch (e) { alert(`Erreur: ${e.message}`); }
                }} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${app.color}`, background: 'transparent', color: app.color, fontWeight: 700, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  ↓ Install
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Barre d'actions groupées */}
      {selectedDevices.length > 0 && (
        <BulkActionsBar theme={theme}>
          <BulkActionsLeft>
            <BulkActionsCount theme={theme}>
              {selectedDevices.length} device{selectedDevices.length > 1 ? 's' : ''} sélectionné{selectedDevices.length > 1 ? 's' : ''}
            </BulkActionsCount>
            <BulkActionsButtons>
              <BulkActionButton theme={theme} onClick={() => setWorkflowModalOpen(true)}>
                <Play size={16} />
                Créer un Workflow
              </BulkActionButton>
            </BulkActionsButtons>
          </BulkActionsLeft>
          <ClearSelectionButton theme={theme} onClick={clearSelection}>
            <X size={16} />
            Tout désélectionner
          </ClearSelectionButton>
        </BulkActionsBar>
      )}

      {viewMode === 'card' ? (
        <CardGrid>
          {sortedDevices.map(device => (
            <DCard key={`${device.device_id}-${device.account_id || 'no-account'}`} $connected={device.isConnected}>
              <DCardHead>
                <DCardName>
                  {/* Profile picture or device icon */}
                  {device.account_username ? (
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <img
                        src={getLocalProfilePicUrl(device.account_username)}
                        alt=""
                        onError={e => {
                          const step = parseInt(e.target.dataset.fb || '0');
                          if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(device.account_username); return; }
                          if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${device.account_username}`; return; }
                          e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                        style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary)' }}
                      />
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--primary)', display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff' }}>
                        {device.account_username.charAt(0).toUpperCase()}
                      </div>
                    </div>
                  ) : (
                    <Smartphone size={16} color="var(--primary)" />
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span>{device.device_name || device.device_id}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {device.account_type && (
                        <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {device.account_type}
                        </span>
                      )}
                      {(() => {
                        const ws = workstations.find(w => w.id === device.workstation_id);
                        if (!ws) return null;
                        const isRpi = (ws.name || '').toLowerCase().includes('raspberry') || (ws.name || '').toLowerCase().includes('rpi');
                        const WsIcon = isRpi ? Server : Monitor;
                        return (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 2,
                            fontSize: 8, fontWeight: 600, color: isRpi ? '#22c55e' : 'var(--info)',
                            background: isRpi ? 'rgba(34,197,94,.1)' : 'rgba(59,130,246,.1)',
                            padding: '1px 5px', borderRadius: 3
                          }}>
                            <WsIcon size={8} />
                            {ws.name}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </DCardName>
                <DCardStatus $on={device.isConnected}>
                  <DCardDot $on={device.isConnected} />
                  {device.isConnected ? 'Connecté' : 'Hors ligne'}
                </DCardStatus>
              </DCardHead>

              <DCardBody>
                <DCardField>
                  Device ID
                  <span title={device.device_id}>{device.device_id}</span>
                </DCardField>
                <DCardField>
                  Workflow
                  <span>{device.workflow ? <DCardWorkflow>{device.workflow}</DCardWorkflow> : 'N/A'}</span>
                </DCardField>
                <DCardField>
                  Compte IG
                  <span title={device.account_username}>
                    {device.account_username ? `@${device.account_username}` : 'Aucun'}
                  </span>
                </DCardField>
                <DCardField>
                  Gmail
                  <span title={device.gmail_account}>
                    {device.gmail_account || 'N/A'}
                  </span>
                </DCardField>
                <DCardField>
                  Consommation
                  <span>
                    {devicePowerStats[device.device_id]?.currentPower 
                      ? `${devicePowerStats[device.device_id].currentPower.toFixed(1)} mW` 
                      : 'N/A'}
                  </span>
                </DCardField>
                <DCardField>
                  IP
                  <span>{device.ip_address || 'N/A'}</span>
                </DCardField>
              </DCardBody>

              <DCardActions>
                <DCardActionBtn 
                  disabled={!device.isConnected} 
                  onClick={() => device.isConnected && openCastModal(device)}
                  title="Screen Cast"
                >
                  <Cast size={14} /> Cast
                </DCardActionBtn>
                <DCardActionBtn 
                  disabled={!device.isConnected}
                  onClick={() => {
                    if (device.isConnected) {
                      axios.post(`/api/restart-device/${device.device_id}`);
                    }
                  }}
                  title="Redémarrer"
                >
                  <RotateCcw size={14} /> Restart
                </DCardActionBtn>
                <DCardActionBtn 
                  onClick={() => {
                    setSelectedInstagramDevice(device);
                    setInstagramModalOpen(true);
                  }}
                  title="Instagram"
                >
                  <Instagram size={14} /> IG
                </DCardActionBtn>
                <DCardActionBtn 
                  onClick={() => {
                    setEditingDevice(device);
                    setEditModalOpen(true);
                  }}
                  title="Modifier"
                >
                  <Settings size={14} />
                </DCardActionBtn>
              </DCardActions>
            </DCard>
          ))}
        </CardGrid>
      ) : (
      <DevicesTable>
        <TableHeader>
          {/* 1. Delete */}
          <TableHeaderCell style={{ textAlign: 'center' }}>
            <Trash2 size={11} />
          </TableHeaderCell>

          {/* 2. Checkbox */}
          <TableHeaderCell>
            <CheckboxCell 
              theme={theme}
              checked={selectedDevices.length === filteredDevices.length && filteredDevices.length > 0}
              onClick={selectedDevices.length === filteredDevices.length ? clearSelection : selectAllDevices}
            >
              {selectedDevices.length === filteredDevices.length && filteredDevices.length > 0 ? (
                <CheckSquare size={16} />
              ) : (
                <Square size={16} />
              )}
            </CheckboxCell>
          </TableHeaderCell>
          
          {/* 3. Status */}
          <TableHeaderCell onClick={() => handleSort('isConnected')} style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
              Statut
              {sortBy === 'isConnected' && (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
            </div>
          </TableHeaderCell>

          {/* 4. Cast */}
          <TableHeaderCell style={{ textAlign: 'center' }}>
            <Cast size={13} />
          </TableHeaderCell>

          {/* 5. Device Name */}
          <TableHeaderCell onClick={() => handleSort('device_name')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              Device Name
              {sortBy === 'device_name' && (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
            </div>
            <SearchInput 
              placeholder="Rechercher..."
              value={searchFilters.device_name || ''}
              onChange={(e) => { e.stopPropagation(); setSearchFilters({...searchFilters, device_name: e.target.value}); }}
              onClick={(e) => e.stopPropagation()}
            />
          </TableHeaderCell>

          {/* 6. Settings */}
          <TableHeaderCell style={{ textAlign: 'center' }}>
            <Settings size={13} />
          </TableHeaderCell>

          {/* 7. Device ID */}
          <TableHeaderCell onClick={() => handleSort('device_id')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              Device ID
              {sortBy === 'device_id' && (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
            </div>
            <SearchInput 
              placeholder="Rechercher..."
              value={searchFilters.device_id || ''}
              onChange={(e) => { e.stopPropagation(); setSearchFilters({...searchFilters, device_id: e.target.value}); }}
              onClick={(e) => e.stopPropagation()}
            />
          </TableHeaderCell>

          {/* 8. Workstation */}
          <TableHeaderCell onClick={() => handleSort('workstation_id')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Monitor size={12} />
              PC
              {sortBy === 'workstation_id' && (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
            </div>
          </TableHeaderCell>

          {/* 9. Compte IG */}
          <TableHeaderCell onClick={() => handleSort('account_username')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Instagram size={12} />
              Compte IG
              {sortBy === 'account_username' && (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
            </div>
            <SearchInput 
              placeholder="Rechercher..."
              value={searchFilters.account_username || ''}
              onChange={(e) => { e.stopPropagation(); setSearchFilters({...searchFilters, account_username: e.target.value}); }}
              onClick={(e) => e.stopPropagation()}
            />
          </TableHeaderCell>

          {/* 10. Gmail */}
          <TableHeaderCell onClick={() => handleSort('gmail_account')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Mail size={12} />
              Gmail
              {sortBy === 'gmail_account' && (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
            </div>
          </TableHeaderCell>

          {/* 11. Workflow */}
          <TableHeaderCell onClick={() => handleSort('workflow')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              Workflow
              {sortBy === 'workflow' && (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
            </div>
          </TableHeaderCell>

          {/* 12. PID/Script */}
          <TableHeaderCell>
            PID/Script
          </TableHeaderCell>
          
          {/* 13. Restart */}
          <TableHeaderCell style={{ textAlign: 'center' }}>
            <RotateCcw size={13} />
          </TableHeaderCell>
        </TableHeader>

        {sortedDevices.map(device => (
          <TableRow 
            key={`${device.device_id}-${device.account_id || 'no-account'}`} 
            connected={device.isConnected}
          >
            {/* 1. Delete */}
            <Cell connected={device.isConnected} style={{ justifyContent: 'center' }}>
              <DeleteButton
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm(`Supprimer "${device.device_name}" (${device.device_id}) ?`)) return;
                  try {
                    const response = await axios.delete(`/api/devices/${device.device_id}`);
                    if (response.data.success) { alert(`✅ Device supprimé!`); fetchDevices(); }
                    else alert(`❌ Erreur: ${response.data.error}`);
                  } catch (error) { alert(`❌ Erreur: ${error.response?.data?.error || error.message}`); }
                }}
                title="Supprimer ce device"
                style={{ padding: 2, minWidth: 0, width: 20, height: 20 }}
              >
                <Trash2 size={11} />
              </DeleteButton>
            </Cell>

            {/* 2. Checkbox */}
            <Cell connected={device.isConnected}>
              <CheckboxCell 
                theme={theme}
                checked={selectedDevices.includes(device.device_id)}
                onClick={(e) => { e.stopPropagation(); toggleDeviceSelection(device.device_id); }}
              >
                {selectedDevices.includes(device.device_id) ? <CheckSquare size={16} /> : <Square size={16} />}
              </CheckboxCell>
            </Cell>

            {/* 3. Statut */}
            <Cell connected={device.isConnected} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <StatusIndicator connected={device.isConnected} />
            </Cell>

            {/* 4. Cast */}
            <Cell connected={device.isConnected} style={{ justifyContent: 'center' }}>
              <CastButton 
                disabled={!device.isConnected}
                onClick={(e) => { e.stopPropagation(); if (device.isConnected) openCastModal(device); }}
                title={device.isConnected ? 'Caster ce device' : 'Device déconnecté'}
                style={{ padding: 2, minWidth: 0, width: 22, height: 22 }}
              >
                <Cast size={13} />
              </CastButton>
            </Cell>

            {/* 5. Device Name */}
            <Cell connected={device.isConnected}>
              <DeviceNameBadge>{device.device_name}</DeviceNameBadge>
            </Cell>

            {/* 6. Settings */}
            <Cell connected={device.isConnected} style={{ justifyContent: 'center' }}>
              <SettingsButton
                onClick={(e) => { e.stopPropagation(); setEditingDevice(device); setEditModalOpen(true); }}
                title="Modifier ce device"
                style={{ padding: 2, minWidth: 0, width: 22, height: 22 }}
              >
                <Settings size={13} />
              </SettingsButton>
            </Cell>

            {/* 7. Device ID */}
            <Cell connected={device.isConnected}>
              <span style={{ fontFamily: 'monospace', fontSize: '10px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={device.device_id}>
                {device.device_id.length > 16 ? device.device_id.substring(0, 16) + '…' : device.device_id}
              </span>
            </Cell>

            {/* 8. Workstation */}
            <Cell connected={device.isConnected}>
              {(() => {
                const ws = workstations.find(w => w.id === device.workstation_id);
                if (!ws) return <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>—</span>;
                const isRpi = (ws.name || '').toLowerCase().includes('raspberry') || (ws.name || '').toLowerCase().includes('rpi');
                const WsIcon = isRpi ? Server : Monitor;
                return (
                  <span title={ws.name} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    fontSize: 9, fontWeight: 600, color: isRpi ? '#22c55e' : 'var(--info)',
                    background: isRpi ? 'rgba(34,197,94,.1)' : 'rgba(59,130,246,.1)',
                    padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap',
                    border: `1px solid ${isRpi ? 'rgba(34,197,94,.2)' : 'rgba(59,130,246,.2)'}`
                  }}>
                    <WsIcon size={9} />
                    {ws.name}
                  </span>
                );
              })()}
            </Cell>

            {/* 9. Compte IG */}
            <Cell connected={device.isConnected}>
              {device.account_username ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <a href={`https://instagram.com/${device.account_username}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    <img 
                      src={getLocalProfilePicUrl(device.account_username)}
                      alt={device.account_username}
                      style={{ width: '24px', height: '24px', borderRadius: '50%', border: '2px solid #E1306C', objectFit: 'cover', cursor: 'pointer' }}
                      onError={(e) => { e.target.src = `https://unavatar.io/instagram/${device.account_username}`; e.target.onerror = () => { e.target.style.display = 'none'; }; }}
                    />
                  </a>
                  <a href={`https://instagram.com/${device.account_username}`} target="_blank" rel="noopener noreferrer" style={{ color: '#E1306C', textDecoration: 'none', fontWeight: '600', fontSize: '12px' }} onClick={(e) => e.stopPropagation()}>
                    @{device.account_username}
                  </a>
                </div>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>
              )}
            </Cell>

            {/* 10. Gmail */}
            <Cell connected={device.isConnected}>
              {device.gmail_account ? (
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={device.gmail_account}>
                  {device.gmail_account}
                </span>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>
              )}
            </Cell>

            {/* 11. Workflow */}
            <Cell connected={device.isConnected}>
              {device.workflow ? (
                <span style={{ 
                  fontSize: '13px', fontWeight: '700',
                  color: { A:'var(--info)', B:'var(--success)', C:'var(--warning)', D:'var(--error)', E:'var(--primary)', F:'var(--accent)', G:'#14b8a6', H:'var(--warning)' }[device.workflow] || 'var(--primary)',
                  background: { A:'rgba(59,130,246,.1)', B:'rgba(16,185,129,.1)', C:'rgba(245,158,11,.1)', D:'rgba(239,68,68,.1)', E:'rgba(165,145,239,.1)', F:'rgba(236,72,153,.1)', G:'rgba(20,184,166,.1)', H:'rgba(249,115,22,.1)' }[device.workflow] || 'rgba(165,145,239,.1)',
                  padding: '2px 8px', borderRadius: '5px',
                  border: `1px solid ${{ A:'rgba(59,130,246,.3)', B:'rgba(16,185,129,.3)', C:'rgba(245,158,11,.3)', D:'rgba(239,68,68,.3)', E:'rgba(165,145,239,.3)', F:'rgba(236,72,153,.3)', G:'rgba(20,184,166,.3)', H:'rgba(249,115,22,.3)' }[device.workflow] || 'rgba(165,145,239,.3)'}`
                }}>
                  {device.workflow}
                </span>
              ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
            </Cell>

            {/* 12. PID/Script */}
            <Cell connected={device.isConnected}>
              {devicePIDs[device.device_id] ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  <span style={{ color: 'var(--success)', fontWeight: '700', fontSize: '11px', fontFamily: 'monospace' }}>
                    PID: {devicePIDs[device.device_id].pid}
                  </span>
                  <span style={{ color: 'var(--primary)', fontSize: '9px', fontWeight: '600' }}>
                    {devicePIDs[device.device_id].scriptType}
                  </span>
                </div>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>
              )}
            </Cell>

            {/* 13. Restart */}
            <Cell connected={device.isConnected} style={{ justifyContent: 'center' }}>
              {device.is_restarter === true && (
                <RestartButton 
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!device.device_id) { alert('❌ Device ID manquant'); return; }
                    if (!window.confirm(`Redémarrer la connexion du device ${device.device_name || device.device_id}?`)) return;
                    try {
                      const response = await axios.post('/api/restart-device', { device_id: device.device_id });
                      if (response.data.success) alert(`✅ Redémarrage lancé pour ${device.device_name || device.device_id}`);
                      else alert(`❌ Erreur: ${response.data.error}`);
                    } catch (error) { alert(`❌ Erreur: ${error.response?.data?.error || error.message}`); }
                  }}
                  disabled={!device.device_id}
                >
                  <RotateCcw size={14} />
                </RestartButton>
              )}
            </Cell>
          </TableRow>
        ))}
      </DevicesTable>
      )}

      {/* Modal d'ajout de device */}
      {addModalOpen && (
        <CastModal onClick={() => setAddModalOpen(false)}>
          <CastWindow onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', height: 'auto' }}>
            <CastHeader>
              <CastTitle>
                <CastDeviceIcon>
                  <Plus size={24} />
                </CastDeviceIcon>
                <CastDeviceInfo>
                  <CastDeviceName>Ajouter un nouveau Device</CastDeviceName>
                  <CastDeviceId>Remplissez les informations ci-dessous</CastDeviceId>
                </CastDeviceInfo>
              </CastTitle>
              <CloseButton onClick={() => setAddModalOpen(false)}>
                <X size={20} />
              </CloseButton>
            </CastHeader>

            <div style={{ padding: '30px' }}>
              <ParamsSection style={{ border: 'none', padding: '0' }}>
                <ParamItem>
                  <ParamLabel style={{ width: '150px' }}>Device ID *</ParamLabel>
                  <input
                    type="text"
                    value={newDevice.device_id}
                    onChange={(e) => setNewDevice({ ...newDevice, device_id: e.target.value })}
                    placeholder="Ex: R58MA4BP37H"
                    style={{
                      flex: 1,
                      padding: '10px 15px',
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                  />
                </ParamItem>

                <ParamItem>
                  <ParamLabel style={{ width: '150px' }}>Device Name *</ParamLabel>
                  <input
                    type="text"
                    value={newDevice.device_name}
                    onChange={(e) => setNewDevice({ ...newDevice, device_name: e.target.value })}
                    placeholder="Ex: SAMSUNG A10 - 1"
                    style={{
                      flex: 1,
                      padding: '10px 15px',
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                  />
                </ParamItem>

                <ParamItem>
                  <ParamLabel style={{ width: '150px' }}>Android Version</ParamLabel>
                  <input
                    type="text"
                    value={newDevice.android_version}
                    onChange={(e) => setNewDevice({ ...newDevice, android_version: e.target.value })}
                    placeholder="Ex: 11"
                    style={{
                      flex: 1,
                      padding: '10px 15px',
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                  />
                </ParamItem>

                <ParamItem>
                  <ParamLabel style={{ width: '150px' }}>Gmail Account</ParamLabel>
                  <input
                    type="email"
                    value={newDevice.gmail_account}
                    onChange={(e) => setNewDevice({ ...newDevice, gmail_account: e.target.value })}
                    placeholder="Ex: exemple@gmail.com"
                    style={{
                      flex: 1,
                      padding: '10px 15px',
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                  />
                </ParamItem>

                <ParamItem>
                  <ParamLabel style={{ width: '150px' }}>Instagram Version</ParamLabel>
                  <input
                    type="text"
                    value={newDevice.instagram_version}
                    onChange={(e) => setNewDevice({ ...newDevice, instagram_version: e.target.value })}
                    placeholder="Ex: 367.0.0.43.89"
                    style={{
                      flex: 1,
                      padding: '10px 15px',
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                  />
                </ParamItem>
              </ParamsSection>

              <div style={{ display: 'flex', gap: '15px', marginTop: '30px' }}>
                <ConnectionBtn 
                  variant="start"
                  onClick={handleAddDevice}
                  style={{ flex: 1 }}
                >
                  <Plus size={16} />
                  Ajouter le Device
                </ConnectionBtn>
                <ConnectionBtn 
                  variant="close"
                  onClick={() => setAddModalOpen(false)}
                  style={{ flex: 1 }}
                >
                  <X size={16} />
                  Annuler
                </ConnectionBtn>
              </div>
            </div>
          </CastWindow>
        </CastModal>
      )}

      {/* Modal d'édition de device */}
      {editModalOpen && editingDevice && (
        <WsAddModal onClick={() => setEditModalOpen(false)}>
          <WsAddCard onClick={(e) => e.stopPropagation()} style={{ width: 520, maxWidth: '95vw' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <Settings size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, color: 'var(--text-primary)' }}>Modifier le Device</h3>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{editingDevice.device_id}</span>
                </div>
              </div>
              <button onClick={() => setEditModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
              <div>
                <label>Device Name *</label>
                <input value={editingDevice.device_name || ''} onChange={(e) => setEditingDevice({ ...editingDevice, device_name: e.target.value })} placeholder="Ex: A10-5" />
              </div>
              <div>
                <label>Android Version</label>
                <input value={editingDevice.android_version || ''} onChange={(e) => setEditingDevice({ ...editingDevice, android_version: e.target.value })} placeholder="Ex: 11" />
              </div>
              <div>
                <label>Gmail Account</label>
                <input type="email" value={editingDevice.gmail_account || ''} onChange={(e) => setEditingDevice({ ...editingDevice, gmail_account: e.target.value })} placeholder="exemple@gmail.com" />
              </div>
              <div>
                <label>Instagram Version</label>
                <input value={editingDevice.instagram_version || ''} onChange={(e) => setEditingDevice({ ...editingDevice, instagram_version: e.target.value })} placeholder="367.0.0.43.89" />
              </div>
              <div style={{ gridColumn: 'span 2', position: 'relative' }}>
                <label>Compte Instagram</label>
                <div
                  onClick={() => setAccountDropdownOpen(v => !v)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 8,
                    border: '1px solid var(--card-border)', background: 'var(--surface)',
                    color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10
                  }}
                >
                  {editingDevice.account_id ? (() => {
                    const sel = availableInstagramAccounts.find(a => String(a.id) === String(editingDevice.account_id));
                    const uname = sel?.username || sel?.instagramUsername || '';
                    return sel ? (<>
                      <img src={getLocalProfilePicUrl(uname)} alt="" onError={e => { e.target.style.display = 'none'; }}
                        style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary)' }} />
                      <span style={{ fontWeight: 700, color: '#fff' }}>@{uname}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>ID: {sel.id}</span>
                    </>) : 'Compte inconnu';
                  })() : <span style={{ color: 'var(--text-muted)' }}>Aucun compte</span>}
                  <ChevronDown size={14} style={{ marginLeft: 'auto', color: 'var(--text-muted)' }} />
                </div>
                {accountDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: 'var(--card-bg)', border: '1px solid var(--primary)',
                    borderRadius: 8, marginTop: 4, maxHeight: 260, overflowY: 'auto',
                    boxShadow: '0 8px 32px rgba(0,0,0,.4)'
                  }}>
                    <div onClick={() => { setEditingDevice({ ...editingDevice, account_id: '' }); setAccountDropdownOpen(false); }}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--card-border)', color: 'var(--text-muted)', fontSize: 12, fontWeight: 600 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(165,145,239,.1)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      Aucun compte
                    </div>
                    {availableInstagramAccounts.map(a => {
                      const uname = a.username || a.instagramUsername || '';
                      return (
                        <div key={a.id}
                          onClick={() => { setEditingDevice({ ...editingDevice, account_id: String(a.id) }); setAccountDropdownOpen(false); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 14px', cursor: 'pointer',
                            borderBottom: '1px solid var(--card-border)',
                            background: String(editingDevice.account_id) === String(a.id) ? 'rgba(165,145,239,.15)' : 'transparent'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(165,145,239,.1)'}
                          onMouseLeave={e => e.currentTarget.style.background = String(editingDevice.account_id) === String(a.id) ? 'rgba(165,145,239,.15)' : 'transparent'}
                        >
                          <img src={getLocalProfilePicUrl(uname)} alt=""
                            onError={e => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                            style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary)', flexShrink: 0 }} />
                          <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--primary)', display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                            {(uname || '?').charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>@{uname || '???'}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>ID: {a.id}{a.account_type ? ` · ${a.account_type}` : ''}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label>Rôle du Device</label>
                <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                  <button onClick={() => setEditingDevice({ ...editingDevice, is_restarter: false })} style={{ flex: 1, padding: '10px 16px', borderRadius: 8, border: `2px solid ${!editingDevice.is_restarter ? 'var(--primary)' : 'var(--card-border)'}`, background: !editingDevice.is_restarter ? 'rgba(165,145,239,.1)' : 'transparent', color: !editingDevice.is_restarter ? 'var(--primary)' : 'var(--text-muted)', fontWeight: 700, fontSize: 12, cursor: 'pointer', transition: 'all .15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Smartphone size={14} /> Worker
                  </button>
                  <button onClick={() => setEditingDevice({ ...editingDevice, is_restarter: true })} style={{ flex: 1, padding: '10px 16px', borderRadius: 8, border: `2px solid ${editingDevice.is_restarter ? 'var(--warning)' : 'var(--card-border)'}`, background: editingDevice.is_restarter ? 'rgba(232,184,74,.1)' : 'transparent', color: editingDevice.is_restarter ? 'var(--warning)' : 'var(--text-muted)', fontWeight: 700, fontSize: 12, cursor: 'pointer', transition: 'all .15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <RotateCcw size={14} /> Restarter
                  </button>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={async () => {
                try {
                  const response = await axios.put(`/api/devices/${editingDevice.device_id}`, {
                    device_name: editingDevice.device_name,
                    android_version: editingDevice.android_version || null,
                    gmail_account: editingDevice.gmail_account || null,
                    instagram_version: editingDevice.instagram_version || null,
                    is_restarter: editingDevice.is_restarter,
                    account_id: editingDevice.account_id || null
                  });
                  if (response.data.success) { setEditModalOpen(false); fetchDevices(); }
                  else alert(`Erreur: ${response.data.error}`);
                } catch (error) { alert(`Erreur: ${error.response?.data?.error || error.message}`); }
              }} style={{ flex: 1, padding: '12px 20px', borderRadius: 10, border: 'none', background: 'var(--gradient)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Settings size={14} /> Enregistrer
              </button>
              <button onClick={() => setEditModalOpen(false)} style={{ padding: '12px 24px', borderRadius: 10, border: '1px solid var(--card-border)', background: 'transparent', color: 'var(--text-muted)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                Annuler
              </button>
            </div>
          </WsAddCard>
        </WsAddModal>
      )}

      {/* Fenêtre contextuelle de casting */}
      {castModalOpen && selectedDevice && (
        <CastModal onClick={closeCastModal}>
          <CastWindow onClick={(e) => e.stopPropagation()}>
            <CastHeader>
              <CastTitle>
                <CastDeviceIcon>
                  <Smartphone size={24} />
                </CastDeviceIcon>
                <CastDeviceInfo>
                  <CastDeviceName>{selectedDevice.device_name}</CastDeviceName>
                  <CastDeviceId>{selectedDevice.device_id}</CastDeviceId>
                </CastDeviceInfo>
              </CastTitle>
              <CloseButton onClick={closeCastModal}>
                <X size={20} />
              </CloseButton>
            </CastHeader>

            <CastContent>
              {/* Panneau gauche - Écran du téléphone */}
              <CastLeftPanel>
                <PhoneScreen
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const resolution = deviceResolutions[selectedDevice.device_id] || { width: 1080, height: 2400 };
                    const x = Math.round((e.clientX - rect.left) * (resolution.width / rect.width));
                    const y = Math.round((e.clientY - rect.top) * (resolution.height / rect.height));
                    setMouseCoords({ x, y, visible: true });
                  }}
                  onMouseLeave={() => {
                    setMouseCoords({ x: 0, y: 0, visible: false });
                  }}
                >
                  <CoordinatesOverlay visible={mouseCoords.visible}>
                    X: {mouseCoords.x} | Y: {mouseCoords.y}
                  </CoordinatesOverlay>
                  <PhoneScreenshot
                    id={`cast-screenshot-${selectedDevice.device_id}`}
                    src={`/api/mjpeg-stream/${selectedDevice.device_id}`}
                    alt={`Screenshot ${selectedDevice.device_id}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      const placeholderSVG = `data:image/svg+xml,${encodeURIComponent(`
                        <svg width="260" height="480" xmlns="http://www.w3.org/2000/svg">
                          <rect width="100%" height="100%" fill="var(--bg)"/>
                          <text x="50%" y="50%" text-anchor="middle" fill="#888" font-family="Arial" font-size="16">
                            Chargement du flux...
                          </text>
                        </svg>
                      `)}`;
                      e.target.src = placeholderSVG;
                    }}
                    onClick={(e) => handleScreenTap(e, selectedDevice.device_id)}
                  />
                </PhoneScreen>
                
                {/* Boutons de navigation sous l'écran */}
                <NavigationButtons>
                  <NavButton onClick={() => handleDeviceAction(selectedDevice.device_id, 'home')}>
                    <Home size={20} />
                    Home
                  </NavButton>
                  <NavButton onClick={() => handleDeviceAction(selectedDevice.device_id, 'back')}>
                    <ArrowLeft size={20} />
                    Retour
                  </NavButton>
                  <NavButton onClick={() => handleDeviceAction(selectedDevice.device_id, 'recent')}>
                    <Grid size={20} />
                    Apps
                  </NavButton>
                </NavigationButtons>
              </CastLeftPanel>

              {/* Colonne de boutons de contrôle au milieu */}
              <CastMiddlePanel>
                <MiddleControlButton 
                  onClick={() => handleDeviceAction(selectedDevice.device_id, 'instagram')}
                  title="Ouvrir Instagram"
                >
                  <Instagram size={24} />
                </MiddleControlButton>

                {runningScripts[selectedDevice.device_id] ? (
                  <MiddleControlButton 
                    onClick={async () => {
                      if (!window.confirm(`Arrêter le script pour ${selectedDevice.device_name}?`)) {
                        return;
                      }
                      
                      try {
                        const result = await callApiForDevice(
                          selectedDevice,
                          '/api/stop-script',
                          'POST',
                          { device_id: selectedDevice.device_id }
                        );
                        
                        if (result?.success) {
                          alert(`✅ Script arrêté pour ${selectedDevice.device_name}!`);
                          fetchScriptStatuses();
                        } else {
                          alert(`❌ Erreur: ${result?.error || 'Unknown error'}`);
                        }
                      } catch (error) {
                        console.error('Erreur arrêt script:', error);
                        alert(`❌ Erreur: ${error.response?.data?.error || error.message}`);
                      }
                    }}
                    title="Arrêter Script"
                    style={{ background: 'linear-gradient(135deg, var(--error), var(--error))' }}
                  >
                    <X size={24} />
                  </MiddleControlButton>
                ) : (
                  <MiddleControlButton 
                    onClick={() => handleDeviceAction(selectedDevice.device_id, 'start_interaction')}
                    title="Lancer Interaction"
                  >
                    <Play size={24} />
                  </MiddleControlButton>
                )}

                <MiddleControlButton 
                  onClick={() => handleDeviceAction(selectedDevice.device_id, 'start_unfollow')}
                  title="Lancer Unfollow"
                >
                  <RotateCcw size={24} />
                </MiddleControlButton>

                <MiddleControlButton 
                  onClick={() => handleDeviceAction(selectedDevice.device_id, 'start_connection')}
                  title="Démarrer Connexion"
                >
                  <Power size={24} />
                </MiddleControlButton>

                <MiddleControlButton 
                  onClick={() => handleDeviceAction(selectedDevice.device_id, 'restart')}
                  title="Redémarrer"
                >
                  <RefreshCw size={24} />
                </MiddleControlButton>

                <MiddleControlButton 
                  onClick={() => handleDeviceAction(selectedDevice.device_id, 'airplane_on')}
                  title="Mode Avion ON"
                >
                  <Plane size={24} />
                </MiddleControlButton>

                <MiddleControlButton 
                  onClick={() => handleDeviceAction(selectedDevice.device_id, 'airplane_off')}
                  title="Mode Avion OFF"
                >
                  <WifiOff size={24} />
                </MiddleControlButton>

                <MiddleControlButton 
                  onClick={() => handleDeviceAction(selectedDevice.device_id, 'close_apps')}
                  title="Fermer Apps"
                >
                  <X size={24} />
                </MiddleControlButton>
              </CastMiddlePanel>

              {/* Panneau droit - Paramètres et stats */}
              <CastRightPanel>
                {/* Colonne 1: Informations Device + Température */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {/* Informations du device */}
                  <ParamsSection>
                    <SectionTitle>
                      <Smartphone size={18} />
                      Informations Device
                    </SectionTitle>
                    
                    <ParamItem>
                      <ParamLabel>
                        <Smartphone size={16} />
                        Android
                      </ParamLabel>
                      <ParamValue>{selectedDevice.android_version || 'N/A'}</ParamValue>
                    </ParamItem>

                    <ParamItem>
                      <ParamLabel>
                        <Wifi size={16} />
                        Adresse IP
                      </ParamLabel>
                      <ParamValue style={{ 
                        fontFamily: 'monospace',
                        color: selectedDevice.ip_address && selectedDevice.ip_address !== 'N/A' ? 'var(--success)' : '#888'
                      }}>
                        {selectedDevice.ip_address || 'N/A'}
                      </ParamValue>
                    </ParamItem>

                    <ParamItem>
                      <ParamLabel>
                        <Mail size={16} />
                        Gmail
                      </ParamLabel>
                      <ParamValue style={{ fontSize: '12px' }}>
                        {selectedDevice.gmail_account || 'N/A'}
                      </ParamValue>
                    </ParamItem>

                    <ParamItem>
                      <ParamLabel>
                        <Instagram size={16} />
                        Compte Instagram
                      </ParamLabel>
                      <ParamValue style={{ 
                        fontSize: '13px',
                        color: selectedDevice.instagramUsername ? 'var(--accent)' : '#888'
                      }}>
                        {selectedDevice.instagramUsername ? `@${selectedDevice.instagramUsername}` : 'N/A'}
                      </ParamValue>
                    </ParamItem>

                    <ParamItem>
                      <ParamLabel>
                        <Package size={16} />
                        Version Instagram
                      </ParamLabel>
                      <ParamValue style={{ fontSize: '12px' }}>
                        {selectedDevice.instagram_version || 'N/A'}
                      </ParamValue>
                    </ParamItem>

                    {selectedDevice.gmail_version && (
                      <ParamItem>
                        <ParamLabel><Mail size={16} /> Version Gmail</ParamLabel>
                        <ParamValue style={{ fontSize: '12px' }}>{selectedDevice.gmail_version}</ParamValue>
                      </ParamItem>
                    )}
                    {selectedDevice.tiktok_version && (
                      <ParamItem>
                        <ParamLabel><Package size={16} /> Version TikTok</ParamLabel>
                        <ParamValue style={{ fontSize: '12px' }}>{selectedDevice.tiktok_version}</ParamValue>
                      </ParamItem>
                    )}
                    {selectedDevice.facebook_version && (
                      <ParamItem>
                        <ParamLabel><Package size={16} /> Version Facebook</ParamLabel>
                        <ParamValue style={{ fontSize: '12px' }}>{selectedDevice.facebook_version}</ParamValue>
                      </ParamItem>
                    )}
                    {selectedDevice.whatsapp_version && (
                      <ParamItem>
                        <ParamLabel><Package size={16} /> Version WhatsApp</ParamLabel>
                        <ParamValue style={{ fontSize: '12px' }}>{selectedDevice.whatsapp_version}</ParamValue>
                      </ParamItem>
                    )}
                  </ParamsSection>

                  {/* Température et stats système */}
                  <ParamsSection>
                    <SectionTitle>
                      <Thermometer size={18} />
                      Température & Performance
                    </SectionTitle>
                  
                  {deviceStats && (
                    <>
                      <ParamItem>
                        <ParamLabel>
                          <Thermometer size={16} />
                          Température
                        </ParamLabel>
                        <ParamValue style={{ 
                          color: deviceStats.temperature > 50 ? 'var(--error)' : 
                                 deviceStats.temperature > 40 ? 'var(--warning)' : 'var(--success)'
                        }}>
                          {deviceStats.temperature}°C
                        </ParamValue>
                      </ParamItem>
                      <TempGauge>
                        <TempFill percentage={(deviceStats.temperature / 60) * 100} />
                      </TempGauge>

                      <ParamItem style={{ marginTop: '15px' }}>
                        <ParamLabel>
                          <Battery size={16} />
                          Batterie
                        </ParamLabel>
                        <ParamValue>{deviceStats.battery}%</ParamValue>
                      </ParamItem>

                      <ParamItem>
                        <ParamLabel>
                          <Cpu size={16} />
                          CPU
                        </ParamLabel>
                        <ParamValue>{deviceStats.cpu}%</ParamValue>
                      </ParamItem>

                      <ParamItem>
                        <ParamLabel>
                          <Activity size={16} />
                          RAM
                        </ParamLabel>
                        <ParamValue>{deviceStats.ram}%</ParamValue>
                      </ParamItem>

                      <ParamItem>
                        <ParamLabel>
                          <HardDrive size={16} />
                          Stockage
                        </ParamLabel>
                        <ParamValue>{deviceStats.storage}%</ParamValue>
                      </ParamItem>

                      <ParamItem style={{ marginTop: '15px', borderTop: '1px solid rgba(165, 145, 239, 0.2)', paddingTop: '15px' }}>
                        <ParamLabel>
                          <Zap size={16} />
                          Consommation
                        </ParamLabel>
                        <ParamValue style={{ 
                          color: deviceStats.power > 5000 ? 'var(--error)' : 
                                 deviceStats.power > 2000 ? 'var(--warning)' : 'var(--success)',
                          fontWeight: '700',
                          fontSize: '15px'
                        }}>
                          {deviceStats.power ? deviceStats.power.toFixed(1) : '0.0'} mW
                        </ParamValue>
                      </ParamItem>

                      {deviceStats.avgPower24h > 0 && (
                        <>
                          <ParamItem>
                            <ParamLabel>
                              <Activity size={16} />
                              Moyenne 24h
                            </ParamLabel>
                            <ParamValue style={{ 
                              fontFamily: 'monospace',
                              fontSize: '13px',
                              color: 'var(--primary)'
                            }}>
                              {deviceStats.avgPower24h.toFixed(1)} mW
                            </ParamValue>
                          </ParamItem>

                          <ParamItem>
                            <ParamLabel>
                              <Battery size={16} />
                              Énergie 24h
                            </ParamLabel>
                            <ParamValue style={{ 
                              fontFamily: 'monospace',
                              fontSize: '13px',
                              color: 'var(--info)'
                            }}>
                              {deviceStats.energyConsumed24h.toFixed(1)} mWh
                            </ParamValue>
                          </ParamItem>
                        </>
                      )}
                    </>
                  )}
                  </ParamsSection>
                </div>

                {/* Colonne 2: Boutons d'action */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <ParamsSection>
                    <SectionTitle>
                      <Zap size={18} />
                      Actions Rapides
                    </SectionTitle>
                    
                    <ActionButton
                      onClick={() => {
                        setSelectedGmailDevice(selectedDevice);
                        setGmailModalOpen(true);
                      }}
                      style={{ background: 'linear-gradient(135deg, var(--error), #D33B2C)' }}
                    >
                      <Mail size={20} />
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14px' }}>Changer Gmail</div>
                        <div style={{ fontSize: '11px', opacity: 0.8 }}>Gérer les comptes Gmail</div>
                      </div>
                    </ActionButton>

                    <ActionButton
                      onClick={() => {
                        setSelectedInstagramDevice(selectedDevice);
                        setInstagramModalOpen(true);
                      }}
                      style={{ background: 'linear-gradient(135deg, #E1306C, #C13584)' }}
                    >
                      <Instagram size={20} />
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14px' }}>Script Instagram</div>
                        <div style={{ fontSize: '11px', opacity: 0.8 }}>Lancer les interactions</div>
                      </div>
                    </ActionButton>

                    <ActionButton
                      onClick={() => {
                        setSelectedGmailDevice(selectedDevice);
                        setGmailModalOpen(true);
                      }}
                      style={{ background: 'linear-gradient(135deg, #4285F4, #3367D6)' }}
                    >
                      <Mail size={20} />
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14px' }}>Script Gmail</div>
                        <div style={{ fontSize: '11px', opacity: 0.8 }}>Lancer Gmail</div>
                      </div>
                    </ActionButton>

                    <ActionButton
                      onClick={() => setApkModalOpen(true)}
                      style={{ background: 'linear-gradient(135deg, var(--primary), var(--secondary))' }}
                    >
                      <Package size={20} />
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14px' }}>Installer APK Instagram</div>
                        <div style={{ fontSize: '11px', opacity: 0.8 }}>Choisir une version</div>
                      </div>
                    </ActionButton>
                  </ParamsSection>

                  {/* Terminal de logs */}
                  <ParamsSection style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    <SectionTitle>
                      <Activity size={18} />
                      Terminal - Logs du Script
                    </SectionTitle>
                    
                    <TerminalContainer>
                      {deviceLogs.length === 0 ? (
                        <LogLine className="info">
                          [INFO] En attente de logs...
                        </LogLine>
                      ) : (
                        deviceLogs.map((log, index) => (
                          <LogLine 
                            key={index}
                            className={
                              log.includes('[ERROR]') || log.includes('❌') ? 'error' :
                              log.includes('[WARNING]') || log.includes('⚠️') ? 'warning' :
                              log.includes('[SUCCESS]') || log.includes('✅') ? 'success' :
                              'info'
                            }
                          >
                            {log}
                          </LogLine>
                        ))
                      )}
                    </TerminalContainer>
                  </ParamsSection>
                </div>
              </CastRightPanel>
            </CastContent>
          </CastWindow>
        </CastModal>
      )}

      {/* Gmail Modal */}
      {gmailModalOpen && selectedGmailDevice && (
        <GmailModal
          device={selectedGmailDevice}
          onClose={() => {
            setGmailModalOpen(false);
            setSelectedGmailDevice(null);
          }}
          onLaunch={handleGmailLaunch}
          onDisconnect={handleGmailDisconnect}
          onUpdateMainAccount={handleUpdateMainGmailAccount}
          theme={theme}
        />
      )}

      {/* Instagram Modal */}
      {instagramModalOpen && selectedInstagramDevice && (
        <InstagramModal
          device={selectedInstagramDevice}
          onClose={() => {
            setInstagramModalOpen(false);
            setSelectedInstagramDevice(null);
          }}
          onLaunch={handleInstagramLaunch}
          theme={theme}
        />
      )}

      {/* Workflow Modal */}
      {workflowModalOpen && selectedDevices.length > 0 && (
        <WorkflowModal
          devices={devices.filter(d => selectedDevices.includes(d.device_id))}
          onClose={() => setWorkflowModalOpen(false)}
          onLaunch={handleWorkflowLaunch}
          theme={theme}
        />
      )}

      {/* APK Installation Modal */}
      {apkModalOpen && (
        <CastModal onClick={() => setApkModalOpen(false)}>
          <CastWindow onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', height: 'auto', maxHeight: '80vh' }}>
            <CastHeader>
              <CastTitle>
                <CastDeviceIcon>
                  <Package size={24} />
                </CastDeviceIcon>
                <CastDeviceInfo>
                  <CastDeviceName>Installer APK Instagram</CastDeviceName>
                  <CastDeviceId>{selectedDevice?.device_name || 'Sélectionner une version'}</CastDeviceId>
                </CastDeviceInfo>
              </CastTitle>
              <CloseButton onClick={() => setApkModalOpen(false)}>
                <X size={20} />
              </CloseButton>
            </CastHeader>

            <div style={{ padding: '30px', maxHeight: '60vh', overflowY: 'auto' }}>
              <ParamsSection style={{ border: 'none', padding: '0' }}>
                <SectionTitle style={{ marginBottom: '20px' }}>
                  <Package size={18} />
                  Versions disponibles
                </SectionTitle>

                {availableApks.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    color: 'var(--text-muted)',
                    fontSize: '14px'
                  }}>
                    <Package size={48} style={{ opacity: 0.3, marginBottom: '15px' }} />
                    <div>Aucune APK disponible</div>
                    <div style={{ fontSize: '12px', marginTop: '10px' }}>
                      Placez vos fichiers APK dans le dossier instagram-apks/
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {availableApks.map((apk, index) => (
                      <ActionButton
                        key={index}
                        onClick={() => setSelectedApk(apk)}
                        style={{ 
                          background: selectedApk === apk 
                            ? 'linear-gradient(135deg, var(--primary), var(--secondary))' 
                            : 'linear-gradient(135deg, rgba(165, 145, 239, 0.2), rgba(168, 85, 247, 0.15))',
                          border: selectedApk === apk ? '2px solid var(--primary)' : '2px solid rgba(165, 145, 239, 0.3)'
                        }}
                      >
                        <Package size={20} />
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '14px' }}>{apk.name}</div>
                          <div style={{ fontSize: '11px', opacity: 0.8 }}>
                            {apk.size} • {apk.date}
                          </div>
                        </div>
                      </ActionButton>
                    ))}
                  </div>
                )}
              </ParamsSection>

              {selectedApk && (
                <div style={{ display: 'flex', gap: '15px', marginTop: '30px' }}>
                  <ConnectionBtn 
                    variant="start"
                    onClick={async () => {
                      if (!selectedDevice) {
                        alert('❌ Aucun device sélectionné');
                        return;
                      }

                      try {
                        const response = await axios.post('/api/devices/install-apk', {
                          device_id: selectedDevice.device_id,
                          apk_filename: selectedApk.filename
                        });
                        
                        if (response.data.success) {
                          alert(`✅ Installation lancée!\n\nAPK: ${selectedApk.name}\nDevice: ${selectedDevice.device_name}\n\nSuivez la progression dans le terminal.`);
                          setApkModalOpen(false);
                          setSelectedApk(null);
                        } else {
                          alert(`❌ Erreur: ${response.data.error}`);
                        }
                      } catch (error) {
                        console.error('Erreur installation APK:', error);
                        alert(`❌ Erreur: ${error.response?.data?.error || error.message}`);
                      }
                    }}
                    style={{ flex: 1 }}
                  >
                    <Package size={16} />
                    Installer sur {selectedDevice?.device_name}
                  </ConnectionBtn>
                  <ConnectionBtn 
                    variant="close"
                    onClick={() => {
                      setApkModalOpen(false);
                      setSelectedApk(null);
                    }}
                    style={{ flex: 1 }}
                  >
                    <X size={16} />
                    Annuler
                  </ConnectionBtn>
                </div>
              )}
            </div>
          </CastWindow>
        </CastModal>
      )}
      {/* ── Add Workstation Modal ── */}
      {wsAddModalOpen && (
        <WsAddModal onClick={() => setWsAddModalOpen(false)}>
          <WsAddCard onClick={e => e.stopPropagation()}>
            <h3><Server size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />Ajouter une Workstation</h3>
            <label>Nom</label>
            <input placeholder="Ex: PC-FIXE" value={newWsName} onChange={e => setNewWsName(e.target.value)} />
            <label>URL WebSocket</label>
            <input placeholder="Ex: ws://192.168.1.100:8083" value={newWsUrl} onChange={e => setNewWsUrl(e.target.value)} />
            <p style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 8 }}>
              Le PC distant doit être connecté au relay (port 8083 par défaut).
            </p>
            <WsAddActions>
              <button onClick={() => setWsAddModalOpen(false)} style={{ background: 'var(--surface)', color: 'var(--text-secondary)' }}>
                Annuler
              </button>
              <button onClick={addWorkstation} style={{ background: 'var(--primary)', color: '#fff' }}>
                Ajouter
              </button>
            </WsAddActions>
          </WsAddCard>
        </WsAddModal>
      )}
    </PageContainer>
  );
};

export default Devices;
